import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Calendar, XCircle, Shirt, RotateCcw, Flag, Settings, Shuffle, UserPlus, UserMinus, Shield, Footprints, ArrowLeft, Megaphone, Minus, Plus, UserCheck, UserX, Ban, MessageCircle, Pencil, Check, X, MoreVertical, Share2, List, ArrowLeftRight, ChevronLeft, Play } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import TeamPicker from '../../components/ui/TeamPicker';
import { getCommunityRating, getRecentRating, calculateGameStats, getVoteBreakdown, getRawVoteBreakdown, parseLocalDate, displayName, distributeNewPlayer } from '../../utils/calculations';
import VoteDetailModal from '../../components/modals/VoteDetailModal';

import Header from '../../components/layout/Header';
import Button from '../../components/ui/Button';
import StarRating from '../../components/ui/StarRating';
import TeamFlag from '../../components/ui/TeamFlag';
import PositionBadge from '../../components/ui/PositionBadge';
import PlayerAvatar from '../../components/ui/PlayerAvatar';
import NumericInput from '../../components/ui/NumericInput';
import SoccerField from '../../components/SoccerField';
import Modal from '../../components/modals/Modal';
import { useToast } from '../../context/ToastContext';
import { TEAMS_OPTIONS } from '../../constants/data';

const GameDetailsPage = () => {
  const { groups, publicGroups, allGames, userId, users, fetchUserProfile, updateGame, finalizeGame, addGuest, setOpenSpots, checkinOpenSpot, approveExternalCheckin, rejectExternalCheckin, cancelGame, reactivateGame, deleteGame, logAuditEvent } = useAppContext();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const { groupId, gameId } = useParams();
  const location = useLocation();
  const handleBack = () => {
    if (location.state?.groupActiveTab) {
      navigate(`/groups/${groupId}`, { state: { activeTab: location.state.groupActiveTab } });
    } else {
      navigate(-1);
    }
  };

  const selectedGame = allGames.find(g => g.id === gameId && g.groupId === groupId);
  const group = groups.find(g => g.id === groupId) || publicGroups.find(g => g.id === groupId);
  const isOrganizer = group?.ownerId === userId || (group?.adminIds || []).includes(userId);
  const isMember = group?.memberIds?.includes(userId) || false;

  const [activeTeamTab, setActiveTeamTab] = useState('A');
  const [activeViewTab, setActiveViewTab] = useState('campo');
  const [showFinishModal, setShowFinishModal] = useState(false);
  const [showDrawSettings, setShowDrawSettings] = useState(false);
  const [showDrawInfo, setShowDrawInfo] = useState(false);
  const [drawConfig, _setDrawConfig] = useState(() => {
    try { const s = localStorage.getItem('drawConfig'); return s ? JSON.parse(s) : { mode: 'balanced_manual' }; } catch { return { mode: 'balanced_manual' }; }
  });
  const setDrawConfig = (val) => {
    const next = typeof val === 'function' ? val(drawConfig) : val;
    try { localStorage.setItem('drawConfig', JSON.stringify(next)); } catch {}
    _setDrawConfig(next);
  };
  const [matchScore, setMatchScore] = useState({ teamA: 0, teamB: 0 });
  // Gols contra creditados a cada time (não atribuídos a nenhum jogador).
  // Explicam quando o placar é maior que a soma dos gols individuais.
  const [ownGoals, setOwnGoals] = useState({ teamA: 0, teamB: 0 });
  const [votingData, setVotingData] = useState({});
  const [isAddingGuest, setIsAddingGuest] = useState(false);
  const [guestForm, setGuestForm] = useState({ name: '', position: 'Meia', manualRating: 5, heartTeam: 'Sem Time' });
  const [showUndoCheckinModal, setShowUndoCheckinModal] = useState(false);
  const [undoAction, setUndoAction] = useState('unconfirm'); // 'unconfirm' | 'markAbsent'
  const [playerStats, setPlayerStats] = useState({});
  const [showStatsInput, setShowStatsInput] = useState(false);
  const [showZeroScoreConfirm, setShowZeroScoreConfirm] = useState(false);
  const [showStatsMismatchConfirm, setShowStatsMismatchConfirm] = useState(false);
  const [statsMismatchInfo, setStatsMismatchInfo] = useState(null); // { teamAGoals, teamBGoals }
  const [incompleteVoteConfirm, setIncompleteVoteConfirm] = useState(null); // { unvotedCount, totalCount } | null
  const [finishTeamTab, setFinishTeamTab] = useState('A'); // 'A' | 'B' — aba ativa no Painel da Partida
  const [nowTick, setNowTick] = useState(Date.now()); // re-render periódico para o cronômetro ao vivo
  // Quando ligado, marcar gol num jogador também soma 1 no placar do time dele
  // (e tirar também subtrai). Default: ligado se ainda está 0×0 ao abrir.
  const [autoCountScore, setAutoCountScore] = useState(true);
  const [showFinalActionConfirm, setShowFinalActionConfirm] = useState(false);
  const [showStopLiveModal, setShowStopLiveModal] = useState(false);
  const [editingDate, setEditingDate] = useState(false);
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');
  const [showCancelVoteMenu, setShowCancelVoteMenu] = useState(false);
  const [editingVotes, setEditingVotes] = useState(false);
  const [skippedPlayers, setSkippedPlayers] = useState(new Set());
  const [showGameMenu, setShowGameMenu] = useState(false);
  const [voteDetailTarget, setVoteDetailTarget] = useState(null);
  const [removePlayerTarget, setRemovePlayerTarget] = useState(null);
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [showForceFinishModal, setShowForceFinishModal] = useState(false);
  const [showRevertVotingModal, setShowRevertVotingModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [votingTab, setVotingTab] = useState('A');
  const [showManualDraw, setShowManualDraw] = useState(false);
  const [manualAssignment, setManualAssignment] = useState({}); // { [playerId]: 'A' | 'B' }
  const [photoModal, setPhotoModal] = useState(null); // player obj | null

  // Carregar perfis dos jogadores confirmados, external checkins e ausentes
  useEffect(() => {
    if (!selectedGame) return;
    const ids = [
      ...(selectedGame.confirmedIds || []),
      ...(selectedGame.externalCheckins || []),
      ...(selectedGame.absentIds || []),
    ];
    ids.forEach(id => {
      if (!users[id] && !id.startsWith('guest_')) fetchUserProfile(id);
    });
  }, [selectedGame, users, fetchUserProfile]);

  // Cronômetro do jogo ao vivo — re-renderiza a cada 30s enquanto houver liveStartedAt
  const liveStartedAt = selectedGame?.liveStartedAt || null;
  useEffect(() => {
    if (!liveStartedAt) return;
    const t = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(t);
  }, [liveStartedAt]);

  if (!selectedGame || !group) {
    return (
      <>
        <Header showBack onBack={handleBack} />
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="text-4xl mb-4">⏳</div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Aguardando confirmacao</h2>
          <p className="text-slate-400 text-sm mb-6">O admin do grupo ainda nao confirmou sua presenca.</p>
          <Button onClick={() => navigate('/')}>Voltar ao Inicio</Button>
        </div>
      </>
    );
  }

  const getPlayer = (uid) => users[uid] || { id: uid, photo: '👤', name: 'Jogador', position: 'Meia', manualRating: 3, heartTeam: 'Sem Time', dominantFoot: 'Destro' };

  // Estado "ao vivo": duração esperada ~60min para sinalizar finalização precoce
  const EXPECTED_MATCH_MIN = 60;
  const isLive = !!selectedGame.liveStartedAt;
  const elapsedMin = isLive ? Math.max(0, Math.floor((nowTick - selectedGame.liveStartedAt) / 60000)) : 0;
  const fmtElapsed = elapsedMin >= 60 ? `${Math.floor(elapsedMin / 60)}h${String(elapsedMin % 60).padStart(2, '0')}` : `${elapsedMin}min`;
  const earlyFinish = isLive && elapsedMin < EXPECTED_MATCH_MIN;

  const handleConfirmPresence = async () => {
    const updates = {
      confirmedIds: [...selectedGame.confirmedIds, userId],
      absentIds: (selectedGame.absentIds || []).filter(id => id !== userId),
      // Limpar status de externo caso o jogador tenha feito checkin externo anteriormente
      externalCheckins: (selectedGame.externalCheckins || []).filter(id => id !== userId),
      confirmedExternalCheckins: (selectedGame.confirmedExternalCheckins || []).filter(id => id !== userId),
    };
    // Se já houve sorteio, distribuir automaticamente no time com menos jogadores
    const newTeams = distributeNewPlayer(selectedGame, { ...getPlayer(userId), id: userId });
    if (newTeams) updates.teams = newTeams;
    await updateGame(groupId, gameId, updates);
  };

  // Desfaz ausência sem confirmar presença (usado quando checkin ainda não abriu)
  const handleUndoAbsent = async () => {
    await updateGame(groupId, gameId, {
      absentIds: (selectedGame.absentIds || []).filter(id => id !== userId),
    });
  };

  // Helper — se o sorteio já foi feito, remove o jogador da escalação dos times
  const teamsWithoutPlayer = (playerId) => {
    if (!selectedGame.teams) return null;
    return {
      teamA: (selectedGame.teams.teamA || []).filter(p => p.id !== playerId),
      teamB: (selectedGame.teams.teamB || []).filter(p => p.id !== playerId),
    };
  };

  const handleUnconfirm = async () => {
    const updates = {
      confirmedIds: selectedGame.confirmedIds.filter(id => id !== userId),
    };
    const newTeams = teamsWithoutPlayer(userId);
    if (newTeams) updates.teams = newTeams;
    await updateGame(groupId, gameId, updates);
  };

  const handleMarkAbsent = async () => {
    const updates = {
      absentIds: [...(selectedGame.absentIds || []).filter(id => id !== userId), userId],
      confirmedIds: selectedGame.confirmedIds.filter(id => id !== userId),
    };
    const newTeams = teamsWithoutPlayer(userId);
    if (newTeams) updates.teams = newTeams;
    await updateGame(groupId, gameId, updates);
  };

  const handleAdminRemovePlayer = async (playerId) => {
    const updates = {
      confirmedIds: selectedGame.confirmedIds.filter(id => id !== playerId),
    };
    const newTeams = teamsWithoutPlayer(playerId);
    if (newTeams) updates.teams = newTeams;
    await updateGame(groupId, gameId, updates);
    setRemovePlayerTarget(null);
    showSuccess('Jogador removido da lista.');
  };

  // Admin confirma presença de um membro pelo admin
  const handleAdminConfirmPlayer = async (playerId) => {
    const player = getPlayer(playerId);
    const updates = {
      confirmedIds: [...selectedGame.confirmedIds, playerId],
      absentIds: (selectedGame.absentIds || []).filter(id => id !== playerId),
      // Limpar status de externo — se era externo e virou membro, promover
      externalCheckins: (selectedGame.externalCheckins || []).filter(id => id !== playerId),
      confirmedExternalCheckins: (selectedGame.confirmedExternalCheckins || []).filter(id => id !== playerId),
    };
    const newTeams = distributeNewPlayer(selectedGame, { ...player, id: playerId });
    if (newTeams) updates.teams = newTeams;
    await updateGame(groupId, gameId, updates);
    showSuccess(`${player.name} confirmado(a)!`);
  };

  // Admin marca membro como ausente
  const handleAdminMarkAbsent = async (playerId) => {
    const player = getPlayer(playerId);
    await updateGame(groupId, gameId, {
      absentIds: [...(selectedGame.absentIds || []).filter(id => id !== playerId), playerId],
      confirmedIds: selectedGame.confirmedIds.filter(id => id !== playerId),
    });
    showSuccess(`${player.name} marcado(a) como ausente.`);
  };

  // Admin marca que um ausente convidou substituto (isenta da penalidade de falta)
  const handleAdminToggleInvitedSub = async (playerId) => {
    const current = selectedGame.invitedSubs || {};
    const newVal = { ...current, [playerId]: !current[playerId] };
    await updateGame(groupId, gameId, { invitedSubs: newVal });
    const player = getPlayer(playerId);
    showSuccess(newVal[playerId]
      ? `${player.name} indicou substituto — falta isenta.`
      : `Isenção de falta removida para ${player.name}.`
    );
  };

  const handleRemoveDuringVoting = async (playerId) => {
    // Remove de confirmedIds e dos times
    const newConfirmedIds = selectedGame.confirmedIds.filter(id => id !== playerId);
    const newTeams = selectedGame.teams ? {
      teamA: (selectedGame.teams.teamA || []).filter(p => p.id !== playerId),
      teamB: (selectedGame.teams.teamB || []).filter(p => p.id !== playerId),
    } : selectedGame.teams;
    // Remove votos que esse jogador recebeu (para não entrar no rating)
    const newVotes = {};
    Object.entries(selectedGame.votes || {}).forEach(([voterId, voteMap]) => {
      const filtered = Object.fromEntries(Object.entries(voteMap).filter(([pid]) => pid !== playerId));
      if (Object.keys(filtered).length > 0) newVotes[voterId] = filtered;
    });
    // Adiciona a ausentes
    const newAbsentIds = [...new Set([...(selectedGame.absentIds || []), playerId])];
    const updates = {
      confirmedIds: newConfirmedIds,
      absentIds: newAbsentIds,
      votes: newVotes,
    };
    if (newTeams) updates.teams = newTeams;
    await updateGame(groupId, gameId, updates);
    setRemovePlayerTarget(null);
    showSuccess('Jogador removido — não receberá nota nesta partida.');
  };

  const handleMovePlayer = async (player, fromTeam) => {
    const fromKey = `team${fromTeam}`;
    const toTeam  = fromTeam === 'A' ? 'B' : 'A';
    const toKey   = `team${toTeam}`;
    const newTeams = {
      teamA: [...(selectedGame.teams.teamA || [])],
      teamB: [...(selectedGame.teams.teamB || [])],
    };
    newTeams[fromKey] = newTeams[fromKey].filter(p => p.id !== player.id);
    newTeams[toKey]   = [...newTeams[toKey], player];
    await updateGame(groupId, gameId, { teams: newTeams });
    showSuccess(`${displayName(player.name, true)} movido para Time ${toTeam}`);
  };

  const handleAddGuest = async (e) => {
    e.preventDefault();
    if (!guestForm.name.trim()) return;
    await addGuest(groupId, gameId, guestForm);
    setGuestForm({ name: '', position: 'Meia', manualRating: 3, heartTeam: 'Sem Time' });
    setIsAddingGuest(false);
  };

  const handleDrawTeams = async () => {
    const confirmedPlayers = selectedGame.confirmedIds.map(id => ({ ...getPlayer(id), id }));

    const getSortingRating = (p) => {
      if (drawConfig.mode === 'balanced_community') {
        if (drawConfig.ratingPeriod === 'recent') return getRecentRating(p) ?? getCommunityRating(p);
        return getCommunityRating(p);
      }
      if (drawConfig.mode === 'balanced_admin') return (group.adminRatings || {})[p.id] ?? getCommunityRating(p);
      return p.manualRating || 3;
    };

    // Quando ignorePositions est? ativo, a distribui??o da linha vira pool ?nico,
    // mas a reserva de goleiros continua valendo: se houver 2+, fica 1 por time.
    const ignorePositions = !!drawConfig.ignorePositions;

    // FASE 1 ? Separar goleiros e distribuir PRIMEIRO (1 por time) em todos os modos.
    // Goleiros 3+ voltam para o pool e s?o distribu?dos como jogadores de linha.
    let teamA = [], teamB = [];
    const goalkeepers = confirmedPlayers.filter(p => p.position === 'Goleiro');
    const outfield = confirmedPlayers.filter(p => p.position !== 'Goleiro');
    const gkSorted = [...goalkeepers].sort((a, b) => getSortingRating(b) - getSortingRating(a));
    if (gkSorted[0] && gkSorted[1]) {
      // Moeda decide qual time recebe o melhor goleiro ? o GK#1 n?o fica sempre
      // com o mesmo grupo de jogadores de linha a cada sorteio
      if (Math.random() < 0.5) { teamA.push(gkSorted[0]); teamB.push(gkSorted[1]); }
      else                      { teamB.push(gkSorted[0]); teamA.push(gkSorted[1]); }
    } else if (gkSorted[0]) {
      teamA.push(gkSorted[0]);
    }
    const remainingGKs = gkSorted.slice(2);
    const toDistribute = [...outfield, ...remainingGKs];

    // FASE 2 — Distribuir o restante conforme o modo escolhido
    if (drawConfig.mode === 'random') {
      const shuffled = [...toDistribute].sort(() => 0.5 - Math.random());
      // Começa pelo time com menos jogadores para manter balanceamento numérico
      let aFirst = teamA.length <= teamB.length;
      shuffled.forEach((player, index) => {
        const goesToA = aFirst ? index % 2 === 0 : index % 2 !== 0;
        (goesToA ? teamA : teamB).push(player);
      });
    } else if (drawConfig.mode === 'heartTeam') {
      // Clássico: torcedores do clubA → Time A, torcedores do clubB → Time B.
      // Demais jogadores (outros clubes + sem time) são distribuídos por rating.
      const clubA = drawConfig.clubA || 'Grêmio';
      const clubB = drawConfig.clubB || 'Internacional';
      const maxPerTeam = Math.ceil(confirmedPlayers.length / 2);

      const fansA = toDistribute.filter(p => p.heartTeam === clubA);
      const fansB = toDistribute.filter(p => p.heartTeam === clubB);
      const others = toDistribute.filter(p => p.heartTeam !== clubA && p.heartTeam !== clubB);

      // Encaixa torcedores do clubA em teamA (excedente vai para os "outros")
      const sortedA = [...fansA].sort((a, b) => getSortingRating(b) - getSortingRating(a));
      const spaceA = maxPerTeam - teamA.length;
      sortedA.slice(0, spaceA).forEach(p => teamA.push(p));
      const excessA = sortedA.slice(spaceA);

      // Encaixa torcedores do clubB em teamB (excedente vai para os "outros")
      const sortedB = [...fansB].sort((a, b) => getSortingRating(b) - getSortingRating(a));
      const spaceB = maxPerTeam - teamB.length;
      sortedB.slice(0, spaceB).forEach(p => teamB.push(p));
      const excessB = sortedB.slice(spaceB);

      // Distribui restantes (outros + excedentes) por pares de rating p/ equilibrar
      const remaining = [...others, ...excessA, ...excessB]
        .sort((a, b) => getSortingRating(b) - getSortingRating(a));
      for (let i = 0; i < remaining.length; i += 2) {
        const p1 = remaining[i], p2 = remaining[i + 1];
        if (!p2) {
          (teamA.length <= teamB.length ? teamA : teamB).push(p1);
        } else {
          if (Math.random() < 0.5) { teamA.push(p1); teamB.push(p2); }
          else { teamB.push(p1); teamA.push(p2); }
        }
      }
    } else if (ignorePositions) {
      // balanced_* sem posições: pool ÚNICO ordenado por rating, pares aleatórios.
      // Mais equilibrado em notas — não há sobras por posição que desbalanceiam.
      const grp = [...toDistribute].sort((a, b) => {
        const diff = getSortingRating(b) - getSortingRating(a);
        return diff !== 0 ? diff : Math.random() - 0.5;
      });
      for (let i = 0; i < grp.length; i += 2) {
        const p1 = grp[i], p2 = grp[i + 1];
        if (!p2) {
          (teamA.length <= teamB.length ? teamA : teamB).push(p1);
        } else {
          if (Math.random() < 0.5) { teamA.push(p1); teamB.push(p2); }
          else                      { teamB.push(p1); teamA.push(p2); }
        }
      }
    } else {
      // balanced_*: agrupa por posição, ordena por rating, pares aleatórios
      // Cada par consecutivo (1º+2º, 3º+4º…) sorteia quem vai para qual time —
      // garante equilíbrio de notas mas varia os times a cada sorteio.
      const posOrder = ['Defensor', 'Meia', 'Atacante']; // Goleiros já distribuídos
      const grouped = {};
      posOrder.forEach(pos => { grouped[pos] = []; });
      toDistribute.forEach(p => {
        const pos = posOrder.includes(p.position) ? p.position : 'Atacante';
        grouped[pos].push(p);
      });
      posOrder.forEach(pos => {
        // Ordena por rating; empates são quebrados aleatoriamente para mais variedade
        const grp = grouped[pos].sort((a, b) => {
          const diff = getSortingRating(b) - getSortingRating(a);
          return diff !== 0 ? diff : Math.random() - 0.5;
        });
        for (let i = 0; i < grp.length; i += 2) {
          const p1 = grp[i];
          const p2 = grp[i + 1];
          if (!p2) {
            // Ímpar: vai pro time com menos jogadores
            (teamA.length <= teamB.length ? teamA : teamB).push(p1);
          } else {
            // Moeda por par: decide qual time recebe o melhor do par
            if (Math.random() < 0.5) { teamA.push(p1); teamB.push(p2); }
            else                      { teamB.push(p1); teamA.push(p2); }
          }
        }
      });
    }

    // FASE 3 — Ordenar por posição (GK primeiro)
    const sortPos = (a, b) => ({'Goleiro':0,'Defensor':1,'Meia':2,'Atacante':3}[a.position] || 9) - ({'Goleiro':0,'Defensor':1,'Meia':2,'Atacante':3}[b.position] || 9);
    teamA.sort(sortPos); teamB.sort(sortPos);

    const ppt = drawConfig.playersPerTeam || selectedGame.playersPerTeam || 6;
    // Persistir o drawConfig no doc do jogo — garante que TODOS os dispositivos vêem o rating
    // do critério usado no sorteio (não o critério local de cada um).
    await updateGame(groupId, gameId, { teams: { teamA, teamB }, playersPerTeam: ppt, drawConfig: { ...drawConfig } });
    setShowDrawSettings(false);
  };

  const handleOpenManualDraw = () => {
    // Pré-popula a partir dos times existentes (se já houve sorteio anterior)
    const assignment = {};
    (selectedGame.teams?.teamA || []).forEach(p => { assignment[p.id] = 'A'; });
    (selectedGame.teams?.teamB || []).forEach(p => { assignment[p.id] = 'B'; });
    setManualAssignment(assignment);
    setShowManualDraw(true);
  };

  const handleSaveManualTeams = async () => {
    const confirmedPlayers = selectedGame.confirmedIds.map(id => ({ ...getPlayer(id), id }));
    const sortPos = (a, b) =>
      ({'Goleiro':0,'Defensor':1,'Meia':2,'Atacante':3}[a.position]||9) -
      ({'Goleiro':0,'Defensor':1,'Meia':2,'Atacante':3}[b.position]||9);
    const teamA = confirmedPlayers.filter(p => manualAssignment[p.id] === 'A').sort(sortPos);
    const teamB = confirmedPlayers.filter(p => manualAssignment[p.id] === 'B').sort(sortPos);
    const ppt = drawConfig.playersPerTeam || selectedGame.playersPerTeam || 6;
    await updateGame(groupId, gameId, {
      teams: { teamA, teamB },
      playersPerTeam: ppt,
      drawConfig: { mode: 'manual', playersPerTeam: ppt },
    });
    setShowManualDraw(false);
    showSuccess('Times salvos!');
  };

  const getStat = (pid, stat) => playerStats[pid]?.[stat] || 0;
  const setStat = (pid, stat, val) => setPlayerStats(prev => ({
    ...prev,
    [pid]: { ...prev[pid], [stat]: Math.max(0, val) },
  }));
  // setGoalAndSync — usado no Painel da Partida: muda gols do jogador e, se
  // autoCountScore está ligado, também ajusta o placar do time correspondente.
  const setGoalAndSync = (pid, newGoals, teamId) => {
    const safeGoals = Math.max(0, newGoals);
    const currentGoals = playerStats[pid]?.goals || 0;
    const delta = safeGoals - currentGoals;
    setPlayerStats(prev => ({
      ...prev,
      [pid]: { ...prev[pid], goals: safeGoals },
    }));
    if (autoCountScore && delta !== 0) {
      setMatchScore(prev => ({
        ...prev,
        [teamId === 'A' ? 'teamA' : 'teamB']: Math.max(0, (prev[teamId === 'A' ? 'teamA' : 'teamB'] || 0) + delta),
      }));
    }
  };

  // Gol contra — ponto creditado a `teamKey` ('teamA'|'teamB') sem atribuir a
  // nenhum jogador. Sempre ajusta o placar (independe do autoCountScore).
  // Os dois setters são chamados separadamente com updaters PUROS — não aninhar
  // um setter dentro do updater do outro (StrictMode invoca o updater 2x e o
  // efeito colateral rodaria em dobro → somava de 2 em 2).
  const addOwnGoal = (teamKey, delta) => {
    const current = ownGoals[teamKey] || 0;
    const applied = Math.max(0, current + delta) - current; // -1, 0 ou +1
    if (applied === 0) return;
    setOwnGoals(prev => ({ ...prev, [teamKey]: Math.max(0, (prev[teamKey] || 0) + applied) }));
    setMatchScore(prev => ({ ...prev, [teamKey]: Math.max(0, (prev[teamKey] || 0) + applied) }));
  };

  // Linha de jogador no Painel da Partida (gols/assistências com +/- grandes).
  // Reutilizada nas abas (mobile) e nas colunas lado a lado (tablet/desktop).
  const renderMatchPlayerRow = (p, teamId) => {
    const goals = getStat(p.id, 'goals');
    const assists = getStat(p.id, 'assists');
    return (
      <div key={p.id} className="flex items-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3">
        <PlayerAvatar user={p} size="sm" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-slate-700 truncate">{displayName(p.name)}</div>
          <div className="flex items-center gap-3 mt-0.5">
            {goals > 0 && <span className="text-[10px] text-emerald-700 font-bold">⚽ {goals}</span>}
            {assists > 0 && <span className="text-[10px] text-blue-700 font-bold">🅰️ {assists}</span>}
            {goals === 0 && assists === 0 && <span className="text-[10px] text-slate-300">sem registros</span>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {/* Gols */}
          <div className="flex items-center gap-0.5 bg-emerald-50 rounded-full p-0.5">
            <button type="button" onClick={() => setGoalAndSync(p.id, goals - 1, teamId)} className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white border border-emerald-100 flex items-center justify-center text-lg font-bold text-slate-400 hover:text-red-500 active:scale-95 transition-all">−</button>
            <div className="px-1 min-w-[1.5rem] text-center text-sm font-bold text-emerald-700">⚽{goals}</div>
            <button type="button" onClick={() => setGoalAndSync(p.id, goals + 1, teamId)} className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-emerald-500 text-white flex items-center justify-center text-lg font-bold hover:bg-emerald-600 active:scale-95 transition-all">+</button>
          </div>
          {/* Assistências */}
          <div className="flex items-center gap-0.5 bg-blue-50 rounded-full p-0.5">
            <button type="button" onClick={() => setStat(p.id, 'assists', Math.max(0, assists - 1))} className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white border border-blue-100 flex items-center justify-center text-lg font-bold text-slate-400 hover:text-red-500 active:scale-95 transition-all">−</button>
            <div className="px-1 min-w-[1.5rem] text-center text-sm font-bold text-blue-700">🅰️{assists}</div>
            <button type="button" onClick={() => setStat(p.id, 'assists', assists + 1)} className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-blue-500 text-white flex items-center justify-center text-lg font-bold hover:bg-blue-600 active:scale-95 transition-all">+</button>
          </div>
        </div>
      </div>
    );
  };

  const handleStartVoting = async () => {
    const statsSummary = {
      playersWithStats: Object.keys(playerStats || {}).length,
      totalGoals: Object.values(playerStats || {}).reduce((sum, stats) => sum + (stats?.goals || 0), 0),
      totalAssists: Object.values(playerStats || {}).reduce((sum, stats) => sum + (stats?.assists || 0), 0),
    };

    if (group.skipVoting) {
      await updateGame(groupId, gameId, { score: matchScore, playerStats, ownGoals });
      await finalizeGame(groupId, gameId);
      setShowFinishModal(false);
      setPlayerStats({});
      setOwnGoals({ teamA: 0, teamB: 0 });
      setShowStatsInput(false);
      return;
    }
    await updateGame(groupId, gameId, { status: 'voting', score: matchScore, playerStats, ownGoals, votes: {} });
    logAuditEvent && logAuditEvent(groupId, 'game.voting_started', gameId, {
      score: matchScore,
      ownGoals,
      statsSummary,
      playerStatsSnapshot: playerStats,
    });
    setShowFinishModal(false);
    setPlayerStats({});
    setOwnGoals({ teamA: 0, teamB: 0 });
    setShowStatsInput(false);
  };

  // Abre o Painel da Partida (live tracking) com defaults adequados.
  const openMatchPanel = () => {
    setAutoCountScore(matchScore.teamA === 0 && matchScore.teamB === 0);
    setShowFinishModal(true);
    if (group.scoringConfig?.requireStats && selectedGame.teams) setShowStatsInput(true);
  };

  // Inicia o jogo "ao vivo": registra a hora de início e abre o Painel.
  const handleStartLive = async () => {
    if (!selectedGame.liveStartedAt) {
      await updateGame(groupId, gameId, { liveStartedAt: Date.now() });
      logAuditEvent && logAuditEvent(groupId, 'game.live_started', gameId, {});
    }
    openMatchPanel();
  };

  // Cancela o "ao vivo" e volta ao estado anterior (times sorteados). Não mexe
  // no placar/stats já marcados — só limpa o cronômetro e fecha o Painel.
  const handleStopLive = async () => {
    await updateGame(groupId, gameId, { liveStartedAt: null });
    logAuditEvent && logAuditEvent(groupId, 'game.live_stopped', gameId, {});
    setShowStopLiveModal(false);
    setShowFinishModal(false);
  };

  // Dispara o fluxo de finalização/votação a partir do Painel (com as validações).
  const triggerFinalize = () => {
    const liveStatsActive = !!selectedGame.teams;
    if (group.scoringConfig?.requireStats && selectedGame.teams && !showStatsInput && !liveStatsActive) { setShowStatsInput(true); return; }
    if (liveStatsActive) {
      const teamAGoals = (selectedGame.teams.teamA || []).reduce((s, p) => s + (playerStats[p.id]?.goals || 0), 0);
      const teamBGoals = (selectedGame.teams.teamB || []).reduce((s, p) => s + (playerStats[p.id]?.goals || 0), 0);
      const hasAnyStats = teamAGoals + teamBGoals > 0;
      // Gols contra já registrados explicam a diferença — somam ao esperado.
      const expectedA = teamAGoals + (ownGoals.teamA || 0);
      const expectedB = teamBGoals + (ownGoals.teamB || 0);
      if (hasAnyStats && (expectedA !== matchScore.teamA || expectedB !== matchScore.teamB)) {
        setStatsMismatchInfo({ teamAGoals, teamBGoals });
        setShowStatsMismatchConfirm(true);
        return;
      }
    }
    if (matchScore.teamA === 0 && matchScore.teamB === 0) { setShowZeroScoreConfirm(true); return; }
    setShowFinalActionConfirm(true);
  };

  const handleSubmitVotes = async () => {
    const _extIds = selectedGame.confirmedExternalCheckins || [];
    const isParticipant = selectedGame.confirmedIds.includes(userId) && !userId.startsWith('guest_') && !_extIds.includes(userId);
    if (!isParticipant) {
      showError('Você não participou deste jogo e não pode votar.');
      return;
    }
    const updatedVotes = { ...selectedGame.votes, [userId]: votingData };
    await updateGame(groupId, gameId, { votes: updatedVotes });
    setVotingData({});
    // Auto-finalizar se todos votaram (avulsos e externos nao votam)
    const confirmedExtIds = selectedGame.confirmedExternalCheckins || [];
    const totalVoters = selectedGame.confirmedIds.filter(id => !id.startsWith('guest_') && !confirmedExtIds.includes(id)).length;
    const votedCount = Object.keys(updatedVotes).length;
    if (votedCount >= totalVoters) {
      await finalizeGame(groupId, gameId);
    }
  };

  const handleFinalize = async () => {
    await finalizeGame(groupId, gameId);
    // Não navega — o listener do Firestore vai atualizar o status para 'finished'
    // e a UI vai renderizar a finished view automaticamente
  };

  // ─── CANCELLED VIEW ───
  if (selectedGame.status === 'cancelled') {
    return (
      <>
        <Header showBack onBack={handleBack} />
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <Ban className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Jogo Cancelado</h2>
          <p className="text-slate-400 text-sm mb-2">{group.name}</p>
          <p className="text-slate-400 text-sm mb-6">
            {parseLocalDate(selectedGame.date).toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })} - {selectedGame.time}
          </p>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={() => navigate('/')}>Voltar</Button>
            {isOrganizer && (
              <Button onClick={async () => { await reactivateGame(groupId, gameId); }}>
                <RotateCcw className="w-4 h-4" /> Reativar Jogo
              </Button>
            )}
            {isOrganizer && (
              <Button variant="danger" onClick={() => setShowDeleteModal(true)}>Excluir</Button>
            )}
          </div>
        </div>
        <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Excluir este jogo?">
          <p className="text-sm text-slate-500 mb-6 text-center">
            O jogo será removido permanentemente. Esta ação não pode ser desfeita.
          </p>
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setShowDeleteModal(false)}>Cancelar</Button>
            <Button variant="danger" className="flex-1" onClick={async () => { setShowDeleteModal(false); await deleteGame(groupId, gameId); showSuccess('Jogo excluído.'); navigate(-1); }}>Excluir</Button>
          </div>
        </Modal>
      </>
    );
  }

  // ─── FINISHED VIEW ───
  if (selectedGame.status === 'finished') {
    const stats = calculateGameStats(selectedGame, users);
    // Conta quantas vezes cada jogador foi Craque (MVP) ou Bola Murcha no histórico do grupo
    const trophyCounts = {}; // { uid: { mvp: N, pereba: N } }
    allGames.forEach(g => {
      if (g.groupId !== groupId || g.status !== 'finished') return;
      const s = calculateGameStats(g, users);
      if (s.mvp) {
        if (!trophyCounts[s.mvp.id]) trophyCounts[s.mvp.id] = { mvp: 0, pereba: 0 };
        trophyCounts[s.mvp.id].mvp += 1;
      }
      if (s.pereba && (!s.mvp || s.pereba.id !== s.mvp.id)) {
        if (!trophyCounts[s.pereba.id]) trophyCounts[s.pereba.id] = { mvp: 0, pereba: 0 };
        trophyCounts[s.pereba.id].pereba += 1;
      }
    });
    return (
      <>
        <Header showBack onBack={handleBack} />
        <div className="flex-1 overflow-y-auto pb-6">
          {/* Score banner — full width, mais baixo, com tag dos times na lateral */}
          <div className="mx-6 mt-6 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-2 px-4 rounded-xl shadow-xl border border-slate-700/70">
            <div className="text-[9px] font-bold uppercase tracking-[0.3em] text-slate-400 text-center">Resultado Final</div>
            <div className="flex items-center justify-center gap-2 mt-0.5">
              <span className="text-[9px] font-black uppercase tracking-widest text-indigo-300 bg-indigo-600/20 border border-indigo-500/40 rounded px-1.5 py-0.5">A</span>
              <span className="text-2xl font-black font-mono text-white leading-none tabular-nums">{selectedGame.score?.teamA ?? 0}</span>
              <span className="text-base font-bold text-slate-500">×</span>
              <span className="text-2xl font-black font-mono text-white leading-none tabular-nums">{selectedGame.score?.teamB ?? 0}</span>
              <span className="text-[9px] font-black uppercase tracking-widest text-orange-300 bg-orange-600/20 border border-orange-500/40 rounded px-1.5 py-0.5">B</span>
            </div>
            <div className="text-slate-400 text-[10px] mt-0.5 text-center truncate">{group.name} · {parseLocalDate(selectedGame.date).toLocaleDateString('pt-BR')}</div>
          </div>

          {/* Destaques da partida — cards estilo EA FC / FUT */}
          {(() => {
            const cards = [];
            if (stats.mvp && stats.sortedPlayers.length > 1) cards.push({
              kind: 'craque', title: 'CRAQUE',
              user: stats.mvp.user, uid: stats.mvp.id,
              value: stats.mvp.avg.toFixed(1), valueLabel: 'NOTA',
              theme: 'gold',
            });
            if (stats.topScorer) cards.push({
              kind: 'artilheiro', title: 'ARTILHEIRO',
              user: stats.topScorer.user, uid: stats.topScorer.id,
              value: stats.topScorer.goals,
              valueLabel: stats.topScorer.goals === 1 ? 'GOL' : 'GOLS',
              theme: 'emerald',
            });
            if (stats.topAssister) cards.push({
              kind: 'garcom', title: 'GARÇOM',
              user: stats.topAssister.user, uid: stats.topAssister.id,
              value: stats.topAssister.assists, valueLabel: 'ASSIST.',
              theme: 'blue',
            });
            if (stats.pereba && stats.mvp && stats.pereba.id !== stats.mvp.id) cards.push({
              kind: 'pereba', title: 'BOLA MURCHA',
              user: stats.pereba.user, uid: stats.pereba.id,
              value: stats.pereba.avg.toFixed(1), valueLabel: 'NOTA',
              theme: 'red',
            });
            if (cards.length === 0) return null;

            // Temas: cada um define base, accent, edge, ring e text colors
            const themes = {
              gold: {
                base: 'linear-gradient(135deg, #fde047 0%, #f59e0b 35%, #92400e 100%)',
                accent: 'radial-gradient(circle at 20% 10%, rgba(255,255,255,0.6), transparent 45%)',
                edge: 'linear-gradient(135deg, rgba(255,255,255,0.5) 0%, transparent 25%, transparent 75%, rgba(0,0,0,0.3) 100%)',
                ringClass: 'ring-2 ring-amber-200',
                textClass: 'text-amber-950',
                valueShadow: '0 2px 8px rgba(0,0,0,0.25)',
                pattern: 'repeating-linear-gradient(135deg, transparent 0 8px, rgba(255,255,255,0.06) 8px 9px)',
              },
              emerald: {
                base: 'linear-gradient(135deg, #6ee7b7 0%, #059669 40%, #064e3b 100%)',
                accent: 'radial-gradient(circle at 80% 20%, rgba(167,243,208,0.5), transparent 50%)',
                edge: 'linear-gradient(135deg, rgba(255,255,255,0.35) 0%, transparent 30%, transparent 70%, rgba(0,0,0,0.4) 100%)',
                ringClass: 'ring-2 ring-emerald-200',
                textClass: 'text-emerald-50',
                valueShadow: '0 2px 12px rgba(0,0,0,0.5)',
                pattern: 'repeating-linear-gradient(90deg, transparent 0 14px, rgba(255,255,255,0.05) 14px 15px)',
              },
              blue: {
                base: 'linear-gradient(135deg, #7dd3fc 0%, #2563eb 40%, #1e1b4b 100%)',
                accent: 'radial-gradient(circle at 30% 80%, rgba(186,230,253,0.4), transparent 50%)',
                edge: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 30%, transparent 70%, rgba(0,0,0,0.45) 100%)',
                ringClass: 'ring-2 ring-sky-200',
                textClass: 'text-sky-50',
                valueShadow: '0 2px 12px rgba(0,0,0,0.55)',
                pattern: 'radial-gradient(circle at 15% 30%, rgba(255,255,255,0.18) 0 1.5px, transparent 2px), radial-gradient(circle at 65% 60%, rgba(255,255,255,0.15) 0 1.5px, transparent 2px), radial-gradient(circle at 85% 25%, rgba(255,255,255,0.12) 0 1.5px, transparent 2px), radial-gradient(circle at 40% 80%, rgba(255,255,255,0.12) 0 1.5px, transparent 2px)',
              },
              red: {
                base: 'linear-gradient(135deg, #fca5a5 0%, #dc2626 35%, #450a0a 100%)',
                accent: 'radial-gradient(circle at 70% 30%, rgba(252,165,165,0.4), transparent 55%)',
                edge: 'linear-gradient(135deg, rgba(255,255,255,0.35) 0%, transparent 30%, transparent 70%, rgba(0,0,0,0.5) 100%)',
                ringClass: 'ring-2 ring-red-200',
                textClass: 'text-red-50',
                valueShadow: '0 2px 12px rgba(0,0,0,0.55)',
                pattern: 'repeating-linear-gradient(120deg, transparent 0 12px, rgba(0,0,0,0.08) 12px 13px), repeating-linear-gradient(60deg, transparent 0 18px, rgba(255,255,255,0.04) 18px 19px)',
              },
            };

            return (
              <div className="mx-6 mt-4">
                <div className={`grid gap-3 ${cards.length === 1 ? 'grid-cols-1 max-w-[180px]' : cards.length === 2 ? 'grid-cols-2 max-w-sm mx-auto' : 'grid-cols-2 lg:grid-cols-4 max-w-3xl mx-auto'}`}>
                  {cards.map(c => {
                    const t = themes[c.theme];
                    const playerProfile = c.user || {};
                    // Stats neste jogo
                    const ps = selectedGame.playerStats?.[c.uid] || {};
                    const gameGoals = ps.goals || 0;
                    const gameAssists = ps.assists || 0;
                    // Média geral (lifetime) + contagem de Melhor/Pior no histórico do grupo
                    const avgOverall = getCommunityRating(playerProfile);
                    const tc = trophyCounts[c.uid] || { mvp: 0, pereba: 0 };
                    const isPereba = c.kind === 'pereba';
                    const trophyLabel = isPereba ? 'PIOR' : 'MELHOR';
                    const trophyCount = isPereba ? tc.pereba : tc.mvp;
                    const isDarkTheme = c.theme !== 'gold';
                    // Qual time o jogador estava nesta partida
                    const inTeamA = (selectedGame.teams?.teamA || []).some(p => p.id === c.uid);
                    const inTeamB = (selectedGame.teams?.teamB || []).some(p => p.id === c.uid);
                    const playerTeam = inTeamA ? 'A' : inTeamB ? 'B' : null;

                    return (
                      <button
                        key={c.kind}
                        onClick={() => navigate(`/players/${c.uid}`)}
                        className={`relative overflow-hidden rounded-2xl ${t.ringClass} shadow-2xl text-left hover:scale-[1.03] active:scale-[0.97] transition-transform aspect-[4/5]`}
                        style={{ background: t.base }}
                      >
                        {/* Pattern */}
                        <div className="absolute inset-0 pointer-events-none" style={{ background: t.pattern, mixBlendMode: 'overlay' }} />
                        {/* Accent glow */}
                        <div className="absolute inset-0 pointer-events-none" style={{ background: t.accent }} />
                        {/* Edge sheen */}
                        <div className="absolute inset-0 pointer-events-none" style={{ background: t.edge }} />
                        {/* Inner border */}
                        <div className="absolute inset-0 rounded-2xl pointer-events-none ring-1 ring-inset ring-white/30" />

                        {/* Conteúdo */}
                        <div className="relative h-full flex flex-col p-2 sm:p-2.5">
                          {/* Topo: NOTA grande à esquerda + posição/crest à direita */}
                          <div className="flex items-start justify-between gap-1">
                            <div className="flex flex-col min-w-0">
                              <div className={`text-xl sm:text-2xl font-black leading-none ${t.textClass}`} style={{ textShadow: t.valueShadow }}>
                                {c.value}
                              </div>
                              <div className={`text-[7px] sm:text-[8px] font-black uppercase tracking-[0.2em] mt-0.5 ${t.textClass} opacity-90`}>
                                {c.valueLabel}
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-0.5 shrink-0">
                              {playerProfile.position && <PositionBadge position={playerProfile.position} />}
                              {playerProfile.heartTeam && playerProfile.heartTeam !== 'Sem Time' && <TeamFlag team={playerProfile.heartTeam} />}
                            </div>
                          </div>

                          {/* Foto centralizada + tag do time logo abaixo */}
                          <div className="flex-1 flex flex-col items-center justify-center min-h-0 my-0.5 gap-1">
                            <div className="ring-2 ring-white/40 shadow-xl rounded-full">
                              <PlayerAvatar user={playerProfile} size="lg" />
                            </div>
                            {playerTeam && (
                              <span
                                className={`text-[6px] sm:text-[7px] font-black tracking-wider px-1 py-px rounded leading-none ${
                                  playerTeam === 'A' ? 'bg-indigo-600 text-white' : 'bg-orange-600 text-white'
                                }`}
                                style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
                              >
                                TIME {playerTeam}
                              </span>
                            )}
                          </div>

                          {/* Nome + título numa "faixa" semi-transparente */}
                          <div className={`text-center rounded-md py-0.5 sm:py-1 px-1 mb-1 ${isDarkTheme ? 'bg-black/25' : 'bg-amber-900/15'}`}>
                            <div className={`font-black text-[11px] sm:text-[13px] leading-tight ${t.textClass} truncate`} style={{ textShadow: t.valueShadow }}>
                              {displayName(playerProfile.name) || '—'}
                            </div>
                            <div className={`text-[7px] sm:text-[9px] font-black uppercase tracking-[0.2em] mt-px ${t.textClass} opacity-85`}>
                              {c.title}
                            </div>
                          </div>

                          {/* Stats footer */}
                          <div className={`${isDarkTheme ? 'bg-black/20' : 'bg-amber-900/10'} rounded-md px-1.5 sm:px-2 py-1 space-y-px sm:space-y-0.5`}>
                            <div className={`flex justify-between text-[8px] sm:text-[9px] ${t.textClass} opacity-90 leading-tight`}>
                              <span><span className="font-black opacity-70">GOLS:</span> <span className="font-black">{gameGoals}</span></span>
                              <span><span className="font-black opacity-70">ASS:</span> <span className="font-black">{gameAssists}</span></span>
                            </div>
                            <div className={`flex justify-between text-[8px] sm:text-[9px] ${t.textClass} opacity-90 leading-tight`}>
                              <span className="font-black opacity-70">MÉDIA:</span>
                              <span className="font-black">{avgOverall != null ? avgOverall.toFixed(1) : '—'}</span>
                            </div>
                            <div className={`flex justify-between text-[8px] sm:text-[9px] ${t.textClass} opacity-90 leading-tight`}>
                              <span className="font-black opacity-70">{trophyLabel}:</span>
                              <span className="font-black">{trophyCount}x</span>
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Ranking */}
          {stats.sortedPlayers.length > 0 && (
            <div className="mx-6 mt-4 bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide">Notas da Galera</h3>
              </div>
              <div className="divide-y divide-slate-50">
                {stats.sortedPlayers.map((p, i) => {
                  const player = getPlayer(p.id);
                  const breakdown = selectedGame.outlierFilterApplied
                    ? getVoteBreakdown(selectedGame, p.id)
                    : getRawVoteBreakdown(selectedGame, p.id);
                  const ps = selectedGame.playerStats?.[p.id] || {};
                  const pGoals = ps.goals || 0;
                  const pAssists = ps.assists || 0;
                  const overallAvg = getCommunityRating(player);
                  return (
                    <button
                      key={p.id}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50 active:bg-slate-100 text-left"
                      onClick={() => setVoteDetailTarget({ player, breakdown })}
                    >
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-slate-200 text-slate-600' : i === 2 ? 'bg-orange-100 text-orange-700' : 'bg-slate-50 text-slate-400'}`}>{i + 1}</div>
                      <PlayerAvatar user={player} size="sm" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-700 truncate">{player.name}</div>
                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                          <PositionBadge position={player.position} />
                          {pGoals > 0 && (
                            <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded-full" title={`${pGoals} gol${pGoals !== 1 ? 's' : ''}`}>
                              ⚽ {pGoals}
                            </span>
                          )}
                          {pAssists > 0 && (
                            <span className="text-[10px] font-bold text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded-full" title={`${pAssists} assistência${pAssists !== 1 ? 's' : ''}`}>
                              🅰️ {pAssists}
                            </span>
                          )}
                          <span className="text-[10px] text-slate-400">Média: {overallAvg != null ? `${overallAvg.toFixed(1)}★` : '—'}</span>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-bold text-slate-700">★ {p.avg.toFixed(1)}</div>
                        <div className="text-[9px] text-slate-400">toque p/ detalhar</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {stats.sortedPlayers.length > 0 && (
            <div className="mx-6 mt-2">
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-[10px] text-slate-400">
                  <span className="font-bold">Critérios de desempate:</span> Nota → Votos Recebidos → Gols → Assistências → Nome
                </p>
              </div>
            </div>
          )}

          {/* Times do dia */}
          {selectedGame.teams && (
            <div className="mx-6 mt-4 bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide flex items-center gap-2"><Shirt className="w-3.5 h-3.5" /> Times</h3>
              </div>
              <div className="grid grid-cols-2 divide-x divide-slate-100">
                <div className="p-4">
                  <div className="text-xs font-bold text-indigo-600 mb-2">Time A</div>
                  <div className="space-y-1.5">
                    {selectedGame.teams.teamA?.map((p, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-xs">
                        <PlayerAvatar user={p} size="xs" />
                        <span className="text-slate-700 truncate">{displayName(p.name)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="p-4">
                  <div className="text-xs font-bold text-orange-600 mb-2">Time B</div>
                  <div className="space-y-1.5">
                    {selectedGame.teams.teamB?.map((p, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-xs">
                        <PlayerAvatar user={p} size="xs" />
                        <span className="text-slate-700 truncate">{displayName(p.name)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mx-6 mt-6 space-y-3">
            {isOrganizer && (
              <p className="text-center text-xs text-slate-400 italic">Jogo finalizado. Dados já computados no histórico.</p>
            )}
            <Button className="w-full" onClick={() => navigate('/')}><ArrowLeft className="w-4 h-4" /> Voltar ao Inicio</Button>
          </div>
        </div>

        <VoteDetailModal
          isOpen={!!voteDetailTarget}
          onClose={() => setVoteDetailTarget(null)}
          player={voteDetailTarget?.player}
          distribution={voteDetailTarget?.breakdown?.distribution}
          outlierVotes={voteDetailTarget?.breakdown?.outlierVotes}
          filteredAvg={voteDetailTarget?.breakdown?.filteredAvg}
          totalVotes={voteDetailTarget?.breakdown?.totalVotes}
          noData={!voteDetailTarget?.breakdown}
        />
      </>
    );
  }

  // ─── VOTING + SCHEDULED VIEWS ───
  return (
    <>
      <Header showBack onBack={handleBack} />
      <div className="flex-1 overflow-y-auto pb-6">
        <div className="px-6 py-4 bg-white border-b border-slate-100 sticky top-0 z-10">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-bold text-slate-800 mb-1">{group.name}</h2>
              {editingDate ? (
                <div className="flex items-center gap-2 mt-1">
                  <input type="date" value={editDate} onChange={e => setEditDate(e.target.value)} className="p-1.5 text-sm bg-slate-50 border border-slate-200 rounded-lg outline-none" />
                  <input type="time" value={editTime} onChange={e => setEditTime(e.target.value)} className="p-1.5 text-sm bg-slate-50 border border-slate-200 rounded-lg outline-none" />
                  <button onClick={async () => { await updateGame(groupId, gameId, { date: editDate, time: editTime }); setEditingDate(false); }} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg"><Check className="w-4 h-4" /></button>
                  <button onClick={() => setEditingDate(false)} className="p-1.5 text-slate-400 hover:bg-slate-50 rounded-lg"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-slate-500 text-sm">
                  <Calendar className="w-4 h-4" /><span>{parseLocalDate(selectedGame.date).toLocaleDateString('pt-BR', {day: '2-digit', month: '2-digit'})}</span>
                  <span className="w-1 h-1 bg-slate-300 rounded-full"></span><span>{selectedGame.time}</span>
                  {isOrganizer && selectedGame.status === 'scheduled' && (
                    <button onClick={() => { setEditDate(selectedGame.date); setEditTime(selectedGame.time); setEditingDate(true); }} className="p-1 text-blue-500 hover:bg-blue-50 rounded-lg ml-1"><Pencil className="w-3.5 h-3.5" /></button>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {selectedGame.status === 'scheduled' && (
                isOrganizer ? (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => updateGame(groupId, gameId, { maxPlayers: Math.max(selectedGame.confirmedIds.length, (selectedGame.maxPlayers || 10) - 1) })}
                      className="w-6 h-6 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                      title="Reduzir vagas"
                    >
                      <Minus className="w-3 h-3 text-slate-500" />
                    </button>
                    <span className={`text-2xl font-bold ${selectedGame.confirmedIds.length >= selectedGame.maxPlayers ? 'text-red-500' : 'text-blue-600'}`}>
                      {selectedGame.confirmedIds.length}
                      <span className="text-slate-300 text-lg font-normal">/{selectedGame.maxPlayers}</span>
                    </span>
                    <button
                      onClick={() => updateGame(groupId, gameId, { maxPlayers: (selectedGame.maxPlayers || 10) + 1 })}
                      className="w-6 h-6 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                      title="Liberar mais vagas"
                    >
                      <Plus className="w-3 h-3 text-slate-500" />
                    </button>
                  </div>
                ) : (
                  <span className={`text-2xl font-bold ${selectedGame.confirmedIds.length >= selectedGame.maxPlayers ? 'text-red-500' : 'text-blue-600'}`}>
                    {selectedGame.confirmedIds.length}
                    <span className="text-slate-300 text-lg font-normal">/{selectedGame.maxPlayers}</span>
                  </span>
                )
              )}
              <button
                onClick={() => {
                  const url = `${window.location.origin}/invite/${groupId}/${gameId}`;
                  if (navigator.share) {
                    navigator.share({ title: `${group.name} - Jogo`, text: 'Bora jogar! ⚽', url }).catch(() => {});
                  } else {
                    navigator.clipboard.writeText(url);
                    showSuccess('Link copiado!');
                  }
                }}
                className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Compartilhar jogo"
              >
                <Share2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {selectedGame.status === 'voting' && (
          <div className="p-6">
            {(() => { const _extIds = selectedGame.confirmedExternalCheckins || []; const voted = Object.keys(selectedGame.votes || {}).length; const total = selectedGame.confirmedIds.filter(id => !id.startsWith('guest_') && !_extIds.includes(id)).length; const remaining = total - voted; return (
            <>
            <div className="bg-indigo-600 text-white p-4 rounded-xl shadow-lg mb-6">
              <div className="flex justify-between items-center mb-2"><h2 className="text-xl font-bold">Fim de Jogo!</h2><span className="text-2xl font-mono">{selectedGame.score.teamA} x {selectedGame.score.teamB}</span></div>
              <p className="text-indigo-100 text-sm">Avalie a galera para atualizar o nivel dos jogadores.</p>
              <div className="mt-3 flex items-center gap-2">
                <div className="flex-1 bg-indigo-500 rounded-full h-2">
                  <div className="bg-white rounded-full h-2 transition-all" style={{ width: `${total > 0 ? (voted / total) * 100 : 0}%` }}></div>
                </div>
                <span className="text-indigo-200 text-xs font-bold">{voted}/{total}</span>
              </div>
              {remaining > 0 && (() => {
                const votedIds = Object.keys(selectedGame.votes || {});
                const pendingPlayers = selectedGame.confirmedIds
                  .filter(id => !id.startsWith('guest_') && !_extIds.includes(id) && !votedIds.includes(id))
                  .map(id => displayName(getPlayer(id).name, true) || 'Jogador');
                return (
                  <div className="mt-2">
                    <p className="text-indigo-200 text-xs">Faltam votar: <span className="font-bold text-white">{pendingPlayers.join(', ')}</span></p>
                  </div>
                );
              })()}
            </div>
            {(() => {
              const _isParticipant = selectedGame.confirmedIds.includes(userId) && !userId.startsWith('guest_') && !_extIds.includes(userId);
              if (!_isParticipant) {
                return (
                  <div className="text-center py-8 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                    <div className="text-3xl mb-3">🚫</div>
                    <p className="font-bold text-slate-700">Você não participou deste jogo</p>
                    <p className="text-sm text-slate-400 mt-1">Apenas jogadores confirmados podem avaliar.</p>
                  </div>
                );
              }
              return (selectedGame.votes?.[userId] && !editingVotes) ? (
              <div className="text-center py-8 bg-slate-50 rounded-xl border border-dashed border-slate-300">
                <div className="text-2xl mb-3">🗳️</div><h3 className="font-bold text-slate-800">Votos Registrados!</h3>
                <p className="text-slate-400 text-sm mt-2">{remaining > 0 ? `Faltam ${remaining} jogador${remaining !== 1 ? 'es' : ''} votar...` : 'Todos votaram! Finalizando...'}</p>
                <button onClick={() => { setVotingData(selectedGame.votes[userId]); setEditingVotes(true); }} className="mt-3 text-sm text-blue-600 font-medium hover:text-blue-700">Editar meus votos</button>
                {isOrganizer && (
                  <div className="px-4 mt-4 flex flex-col gap-2">
                    {remaining > 0 && <Button onClick={() => setShowFinalizeModal(true)} className="w-full">Finalizar & Atualizar Níveis</Button>}
                    <div className="relative">
                      <button onClick={() => setShowCancelVoteMenu(s => !s)} className="w-full text-xs text-slate-400 hover:text-red-500 py-2 transition-colors">Cancelar Votação ▾</button>
                      {showCancelVoteMenu && (
                        <div className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden z-20">
                          <button onClick={() => { setShowCancelVoteMenu(false); setShowRevertVotingModal(true); }} className="w-full text-left px-4 py-3 text-sm text-slate-600 hover:bg-slate-50 border-b border-slate-100">
                            Voltar para Agendado
                          </button>
                          <button onClick={() => { setShowCancelVoteMenu(false); setShowForceFinishModal(true); }} className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50">
                            Finalizar sem Votação
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <h3 className="font-bold text-slate-800 border-b border-slate-100 pb-2">{editingVotes ? 'Editar Avaliações' : 'Avalie os Jogadores'}</h3>
                {(() => {
                  const votableIds = selectedGame.confirmedIds.filter(id => id !== userId && !id.startsWith('guest_') && !_extIds.includes(id));
                  const teamAIds = selectedGame.teams?.teamA?.map(p => p.id) || [];
                  const teamBIds = selectedGame.teams?.teamB?.map(p => p.id) || [];
                  const inTeamA = votableIds.filter(id => teamAIds.includes(id));
                  const inTeamB = votableIds.filter(id => teamBIds.includes(id));
                  const noTeam  = votableIds.filter(id => !teamAIds.includes(id) && !teamBIds.includes(id));
                  const renderPlayer = (pid) => {
                    const p = getPlayer(pid);
                    const isSkipped = skippedPlayers.has(pid);
                    const pStats = selectedGame.playerStats?.[pid] || {};
                    const pGoals = pStats.goals || 0;
                    const pAssists = pStats.assists || 0;
                    return (
                      <div key={pid} className={`p-3 rounded-xl border shadow-sm ${isSkipped ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-100'}`}>
                        {/* Linha 1: avatar (clicável) + nome + posição + time + Desfazer */}
                        <div className="flex items-center gap-2 mb-2">
                          <button
                            onClick={() => setPhotoModal(p)}
                            className="shrink-0 rounded-full ring-2 ring-transparent hover:ring-blue-300 transition-all"
                            title="Ver foto"
                          >
                            <PlayerAvatar user={p} size="sm" />
                          </button>
                          <div className="flex-1 min-w-0">
                            <div className={`text-sm font-bold leading-tight ${isSkipped ? 'text-slate-400' : 'text-slate-700'}`}>{displayName(p.name)}</div>
                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                              {p.position && <PositionBadge position={p.position} />}
                              {p.heartTeam && p.heartTeam !== 'Sem Time' && <TeamFlag team={p.heartTeam} />}
                              {pGoals > 0 && (
                                <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded-full" title={`${pGoals} gol${pGoals !== 1 ? 's' : ''}`}>
                                  ⚽ {pGoals}
                                </span>
                              )}
                              {pAssists > 0 && (
                                <span className="text-[10px] font-bold text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded-full" title={`${pAssists} assistência${pAssists !== 1 ? 's' : ''}`}>
                                  🅰️ {pAssists}
                                </span>
                              )}
                            </div>
                          </div>
                          {isSkipped && (
                            <button onClick={() => { const next = new Set(skippedPlayers); next.delete(pid); setSkippedPlayers(next); }} className="text-xs text-blue-500 font-medium hover:text-blue-700 shrink-0">Desfazer</button>
                          )}
                          {isOrganizer && !pid.startsWith('guest_') && (
                            <button
                              onClick={() => setRemovePlayerTarget({ id: pid, name: displayName(p.name) })}
                              className="shrink-0 p-1 text-slate-300 hover:text-red-500 transition-colors"
                              title="Remover jogador (não apareceu)"
                            >
                              <UserMinus size={15} />
                            </button>
                          )}
                        </div>
                        {/* Linha 2: estrelas centralizadas (ou status se skipped) */}
                        {!isSkipped ? (
                          <div className="flex justify-center">
                            <StarRating rating={votingData[pid] || 0} setRating={(val) => setVotingData({...votingData, [pid]: val})} />
                          </div>
                        ) : (
                          <div className="text-[10px] text-slate-400 text-center">Não avaliado</div>
                        )}
                        {!isSkipped && !votingData[pid] && (
                          <div className="text-center mt-2">
                            <button onClick={() => { const next = new Set(skippedPlayers); next.add(pid); setSkippedPlayers(next); const d = {...votingData}; delete d[pid]; setVotingData(d); }} className="text-[11px] text-slate-400 hover:text-slate-600 font-medium">Não sei avaliar</button>
                          </div>
                        )}
                      </div>
                    );
                  };
                  if (votableIds.length === 0) {
                    return (
                      <div className="text-center py-8 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                        <div className="text-3xl mb-3">🏁</div>
                        <p className="font-bold text-slate-700">Nenhum jogador para avaliar</p>
                        <p className="text-sm text-slate-400 mt-1 mb-4">Todos os participantes são externos ou convidados.</p>
                        {isOrganizer && <Button onClick={() => setShowFinalizeModal(true)} className="mx-auto">Encerrar Jogo</Button>}
                      </div>
                    );
                  }
                  // Com times sorteados: abas Time A / Time B / Sem Time
                  if (inTeamA.length > 0 || inTeamB.length > 0) {
                    const tabs = [
                      ...(inTeamA.length > 0 ? [{ id: 'A', label: 'Time A', color: 'blue',   ids: inTeamA }] : []),
                      ...(inTeamB.length > 0 ? [{ id: 'B', label: 'Time B', color: 'orange', ids: inTeamB }] : []),
                      ...(noTeam.length  > 0 ? [{ id: 'X', label: 'Outros', color: 'slate',  ids: noTeam  }] : []),
                    ];
                    const activeTab = tabs.find(t => t.id === votingTab) || tabs[0];
                    const colorActive = { blue: 'bg-blue-600 text-white', orange: 'bg-orange-500 text-white', slate: 'bg-slate-500 text-white' };
                    const colorInactive = { blue: 'text-blue-600 bg-blue-50 hover:bg-blue-100', orange: 'text-orange-500 bg-orange-50 hover:bg-orange-100', slate: 'text-slate-500 bg-slate-100 hover:bg-slate-200' };
                    // Contagem de avaliados por aba (para badge)
                    const doneCount = (ids) => ids.filter(id => votingData[id] || skippedPlayers.has(id)).length;
                    return (
                      <>
                        {/* Tabs */}
                        <div className="flex gap-2">
                          {tabs.map(t => (
                            <button key={t.id} onClick={() => setVotingTab(t.id)}
                              className={`flex-1 py-2 px-3 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-1.5 ${t.id === activeTab.id ? colorActive[t.color] : colorInactive[t.color]}`}>
                              {t.label}
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${t.id === activeTab.id ? 'bg-white/20' : 'bg-white'}`}>
                                {doneCount(t.ids)}/{t.ids.length}
                              </span>
                            </button>
                          ))}
                        </div>
                        {/* Lista da aba activa */}
                        <div className="space-y-3">
                          {activeTab.ids.map(renderPlayer)}
                        </div>
                      </>
                    );
                  }
                  // Sem times sorteados: lista plana
                  return <div className="space-y-3">{votableIds.map(renderPlayer)}</div>;
                })()}
                <Button onClick={() => {
                  // Verifica se há jogadores sem voto e sem skip — pode ter ficado em
                  // outra aba de time que o user esqueceu
                  const votableIds = selectedGame.confirmedIds.filter(id => id !== userId && !id.startsWith('guest_') && !_extIds.includes(id));
                  const unvoted = votableIds.filter(id => !votingData[id] && !skippedPlayers.has(id));
                  if (unvoted.length > 0 && !editingVotes) {
                    setIncompleteVoteConfirm({ unvotedCount: unvoted.length, totalCount: votableIds.length });
                    return;
                  }
                  handleSubmitVotes();
                  setEditingVotes(false);
                }} disabled={Object.keys(votingData).length === 0 && skippedPlayers.size === 0} className="w-full py-4 mt-4">{editingVotes ? 'Atualizar Avaliações' : 'Enviar Avaliações'}</Button>
                {editingVotes && <button onClick={() => setEditingVotes(false)} className="w-full text-sm text-slate-400 hover:text-slate-600 py-2">Cancelar</button>}
                {isOrganizer && !editingVotes && (
                  <div className="relative mt-2">
                    <button onClick={() => setShowCancelVoteMenu(s => !s)} className="w-full text-xs text-slate-400 hover:text-red-500 py-2 transition-colors">Cancelar Votação ▾</button>
                    {showCancelVoteMenu && (
                      <div className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden z-20">
                        <button onClick={async () => { await updateGame(groupId, gameId, { status: 'scheduled', votes: {} }); setShowCancelVoteMenu(false); }} className="w-full text-left px-4 py-3 text-sm text-slate-600 hover:bg-slate-50 border-b border-slate-100">
                          Voltar para Agendado
                        </button>
                        <button onClick={() => { setShowCancelVoteMenu(false); setShowForceFinishModal(true); }} className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50">
                          Finalizar sem Votação
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
            })()}
            </>
            ); })()}
          </div>
        )}

        {selectedGame.status === 'scheduled' && (
          <div className="p-6">
            <div className="mb-8">
              {isMember ? (
                (() => {
                  const isConfirmed = selectedGame.confirmedIds.includes(userId);
                  const isAbsent = (selectedGame.absentIds || []).includes(userId);
                  const isFull = !isConfirmed && selectedGame.confirmedIds.length >= selectedGame.maxPlayers;
                  // checkinHoursBefore: bloqueia checkin se ainda não chegou a hora
                  const checkinHours = group.checkinHoursBefore || 0;
                  let checkinOpen = true;
                  let checkinOpensIn = '';
                  if (checkinHours > 0) {
                    const [h, m] = (selectedGame.time || '00:00').split(':').map(Number);
                    const gameDate = selectedGame.date.replace(/-/g, '/');
                    const gameTs = new Date(`${gameDate} ${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:00`).getTime();
                    const openTs = gameTs - checkinHours * 3600000;
                    if (Date.now() < openTs) {
                      checkinOpen = false;
                      const diffMs = openTs - Date.now();
                      const diffH = Math.floor(diffMs / 3600000);
                      const diffMin = Math.floor((diffMs % 3600000) / 60000);
                      if (diffH >= 48) {
                        const diffDays = Math.floor(diffH / 24);
                        const remH = diffH % 24;
                        checkinOpensIn = remH > 0 ? `${diffDays}d ${remH}h` : `${diffDays}d`;
                      } else if (diffH > 0) {
                        checkinOpensIn = diffMin > 0 ? `${diffH}h ${diffMin}min` : `${diffH}h`;
                      } else {
                        checkinOpensIn = `${diffMin}min`;
                      }
                    }
                  }
                  return (
                    <div>
                      {/* Botão principal — confirmar */}
                      {isConfirmed ? (
                        <button
                          className="w-full py-4 bg-emerald-500 text-white font-bold rounded-xl flex items-center justify-center gap-2 hover:bg-emerald-600 transition-colors shadow-lg shadow-emerald-200 text-lg"
                          onClick={() => { setUndoAction('unconfirm'); setShowUndoCheckinModal(true); }}
                        >
                          <Check className="w-5 h-5" /> Confirmado
                        </button>
                      ) : (
                        <Button
                          className="w-full py-4 text-lg shadow-blue-200 shadow-lg"
                          onClick={handleConfirmPresence}
                          disabled={isFull || !checkinOpen}
                        >
                          {isFull ? 'Lista de Espera' : !checkinOpen ? `Abre em ${checkinOpensIn}` : 'Confirmar Presença'}
                        </Button>
                      )}
                      {/* Não vou — discreto, abaixo do botão principal */}
                      <div className="mt-1">
                        {isAbsent ? (
                          <div className="w-full py-3 flex items-center justify-center gap-1.5 text-xs text-red-400">
                            <XCircle className="w-3.5 h-3.5" /> Ausência registrada ·{' '}
                            <button
                              className="underline hover:text-slate-500 transition-colors py-1 px-1"
                              onClick={checkinOpen ? handleConfirmPresence : handleUndoAbsent}
                            >
                              desfazer
                            </button>
                          </div>
                        ) : (
                          <button
                            className="w-full py-3 text-xs text-slate-400 hover:text-red-400 transition-colors"
                            onClick={() => {
                              if (isConfirmed) { setUndoAction('markAbsent'); setShowUndoCheckinModal(true); }
                              else handleMarkAbsent();
                            }}
                          >
                            Não vou neste jogo
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()
              ) : (
                selectedGame.externalCheckins?.includes(userId) ? (
                  <div className="text-center py-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <div className="text-lg mb-1">⏳</div>
                    <p className="text-sm font-medium text-amber-700">Aguardando confirmação do admin</p>
                    <p className="text-xs text-amber-500 mt-1">Você solicitou uma vaga temporária</p>
                  </div>
                ) : (selectedGame.openSpots || 0) > 0 ? (
                  <Button className="w-full py-4 text-lg bg-amber-500 hover:bg-amber-600 shadow-amber-200 shadow-lg" onClick={() => checkinOpenSpot(groupId, gameId)}>
                    ⚡ Quero uma vaga temporária!
                  </Button>
                ) : (
                  <div className="text-center py-4 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-400">
                    Sem vagas abertas no momento
                  </div>
                )
              )}
            </div>

            {/* Vagas abertas — admin pode abrir vagas para nao-membros */}
            {isOrganizer && (
              <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Megaphone className="w-4 h-4 text-amber-600" />
                    <div>
                      <div className="text-sm font-medium text-slate-700">Vagas Abertas</div>
                      <div className="text-[10px] text-slate-400">Convida jogadores de fora</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setOpenSpots(groupId, gameId, Math.max(0, (selectedGame.openSpots || 0) - 1), selectedGame.openSpotsPositions)} className="w-7 h-7 rounded-full bg-white border border-amber-200 flex items-center justify-center hover:bg-amber-100 transition-colors"><Minus className="w-3 h-3 text-amber-700" /></button>
                    <span className="w-8 text-center font-bold text-amber-700">{selectedGame.openSpots || 0}</span>
                    <button onClick={() => setOpenSpots(groupId, gameId, (selectedGame.openSpots || 0) + 1, selectedGame.openSpotsPositions)} className="w-7 h-7 rounded-full bg-white border border-amber-200 flex items-center justify-center hover:bg-amber-100 transition-colors"><Plus className="w-3 h-3 text-amber-700" /></button>
                  </div>
                </div>
                {(selectedGame.openSpots || 0) > 0 && (
                  <div className="mt-3 pt-3 border-t border-amber-200">
                    <div className="text-[10px] text-amber-600 font-bold uppercase mb-2">Posições necessárias</div>
                    <div className="flex flex-wrap gap-2">
                      {['Goleiro', 'Defensor', 'Meia', 'Atacante'].map(pos => {
                        const currentPositions = selectedGame.openSpotsPositions || [];
                        const isSelected = currentPositions.includes(pos);
                        return (
                          <button
                            key={pos}
                            type="button"
                            onClick={() => {
                              const next = isSelected ? currentPositions.filter(p => p !== pos) : [...currentPositions, pos];
                              setOpenSpots(groupId, gameId, selectedGame.openSpots || 0, next);
                            }}
                            className={`px-3 py-1 rounded-lg text-xs font-bold border transition-colors ${isSelected ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-700 border-amber-200 hover:bg-amber-50'}`}
                          >
                            {pos}
                          </button>
                        );
                      })}
                    </div>
                    {(selectedGame.openSpotsPositions?.length || 0) > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        <span className="text-[10px] text-amber-600 font-medium">Falta: </span>
                        {selectedGame.openSpotsPositions.map((pos, i) => (
                          <PositionBadge key={i} position={pos} />
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {selectedGame.externalCheckins?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-amber-200">
                    <div className="text-[10px] text-amber-600 font-bold uppercase mb-2">Check-ins externos ({selectedGame.externalCheckins.length})</div>
                    <div className="flex flex-wrap gap-2">
                      {selectedGame.externalCheckins.map(uid => {
                        const p = users[uid] || { photo: '👤', name: 'Jogador' };
                        return <span key={uid} className="flex items-center gap-1 text-xs bg-white px-2 py-1 rounded-lg border border-amber-100"><PlayerAvatar user={p} size="xs" /> {displayName(p.name)}</span>;
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Menu ⋮ do organizador */}
            {isOrganizer && !selectedGame.teams && (
              <div className="mb-6 relative flex justify-end">
                <button
                  onClick={() => setShowGameMenu(m => !m)}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <MoreVertical className="w-5 h-5" />
                </button>
                {showGameMenu && (
                  <div className="absolute right-0 top-10 bg-white border border-slate-200 rounded-xl shadow-lg z-30 min-w-[180px] py-1">
                    <button
                      onClick={async () => { setShowGameMenu(false); await cancelGame(groupId, gameId); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-red-500 hover:bg-red-50 text-sm font-medium border-b border-slate-100"
                    >
                      <Ban className="w-4 h-4" /> Cancelar Jogo
                    </button>
                    <button
                      onClick={() => { setShowGameMenu(false); setShowDeleteModal(true); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-red-700 hover:bg-red-50 text-sm font-medium"
                    >
                      <XCircle className="w-4 h-4" /> Excluir Jogo
                    </button>
                  </div>
                )}
              </div>
            )}

            {selectedGame.teams ? (
              <div className="mb-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-bold text-slate-800 flex items-center gap-2"><Shirt className="w-5 h-5 text-indigo-500" /> Times em Campo</h3>
                  {isOrganizer && (
                    <div className="flex gap-2 items-center">
                      <button onClick={() => { updateGame(groupId, gameId, { teams: null }); setActiveViewTab('campo'); setActiveTeamTab('A'); }} className="text-xs text-red-500 font-medium hover:bg-red-50 px-2 py-1 rounded flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Desfazer</button>
                      {isLive ? (
                        <button onClick={openMatchPanel} className="text-xs text-white font-bold bg-red-600 px-3 py-1 rounded-lg flex items-center gap-1.5 shadow-sm hover:bg-red-700">
                          <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> AO VIVO · {fmtElapsed}
                        </button>
                      ) : (
                        <>
                          <button onClick={handleStartLive} className="text-xs text-white font-bold bg-emerald-600 px-3 py-1 rounded-lg flex items-center gap-1 shadow-sm hover:bg-emerald-700">
                            <Play className="w-3 h-3" /> Iniciar Ao Vivo
                          </button>
                          <button onClick={openMatchPanel} className="text-xs text-slate-500 font-medium hover:bg-slate-100 px-2 py-1 rounded flex items-center gap-1" title="Lançar resultado direto (jogo já aconteceu)">
                            <Flag className="w-3 h-3" /> Finalizar
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
                {/* Tabs: Time A / Time B */}
                <div className="flex p-1 bg-slate-100 rounded-xl mb-3">{['A', 'B'].map(team => (<button key={team} onClick={() => setActiveTeamTab(team)} className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${activeTeamTab === team ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>Time {team}</button>))}</div>
                {/* Sub-tabs: Campo / Elenco */}
                <div className="flex gap-1 mb-3">
                  {[
                    { id: 'campo', label: 'Campo', icon: <Shirt className="w-3 h-3" /> },
                    { id: 'elenco', label: 'Elenco', icon: <List className="w-3 h-3" /> },
                  ].map(v => (
                    <button key={v.id} onClick={() => setActiveViewTab(v.id)}
                      className={`flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${activeViewTab === v.id ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                      {v.icon} {v.label}
                    </button>
                  ))}
                </div>
                {activeViewTab === 'campo' ? (
                  <div className="lg:max-w-md lg:mx-auto">
                    <SoccerField
                      players={selectedGame.teams[`team${activeTeamTab}`]}
                      playersPerTeam={selectedGame.playersPerTeam || 6}
                      teamLabel={`Time ${activeTeamTab}`}
                      groupName={group.name}
                      gameDate={selectedGame.date}
                      gameTime={selectedGame.time}
                      teams={selectedGame.teams}
                      confirmedIds={selectedGame.confirmedIds}
                      getRating={p => {
                        // Usa o drawConfig persistido no jogo (fonte de verdade compartilhada);
                        // cai no local apenas quando o sorteio é legacy (sem drawConfig salvo).
                        const cfg = selectedGame.drawConfig || drawConfig;
                        if (cfg.mode === 'balanced_community') {
                          if (cfg.ratingPeriod === 'recent') return getRecentRating(p) ?? getCommunityRating(p);
                          return getCommunityRating(p);
                        }
                        if (cfg.mode === 'balanced_admin') return (group.adminRatings || {})[p.id] ?? getCommunityRating(p);
                        return p.manualRating || 3;
                      }}
                    />
                  </div>
                ) : (
                  /* Elenco — lista detalhada */
                  (() => {
                    const teamPlayers = selectedGame.teams[`team${activeTeamTab}`] || [];
                    const ppt = selectedGame.playersPerTeam || 6;
                    const effectiveCfg = selectedGame.drawConfig || drawConfig;
                    const ratingLabel = {
                      balanced_manual: 'Manual',
                      balanced_community: effectiveCfg.ratingPeriod === 'recent' ? 'Galera (4j)' : 'Galera',
                      balanced_admin: 'Admin',
                      random: 'Manual',
                      heartTeam: 'Manual',
                    }[effectiveCfg.mode] || 'Manual';
                    const getR = (p) => {
                      if (effectiveCfg.mode === 'balanced_community') {
                        if (effectiveCfg.ratingPeriod === 'recent') return getRecentRating(p) ?? getCommunityRating(p);
                        return getCommunityRating(p);
                      }
                      if (effectiveCfg.mode === 'balanced_admin') return (group.adminRatings || {})[p.id] ?? getCommunityRating(p);
                      return p.manualRating || 3;
                    };
                    // Mesma ordenação do SoccerField: GK primeiro, depois por ordem de confirmação
                    const sorted = [...teamPlayers].sort((a, b) => {
                      const aGK = a.position === 'Goleiro' ? 0 : 1;
                      const bGK = b.position === 'Goleiro' ? 0 : 1;
                      if (aGK !== bGK) return aGK - bGK;
                      const ia = (selectedGame.confirmedIds || []).indexOf(a.id);
                      const ib = (selectedGame.confirmedIds || []).indexOf(b.id);
                      return (ia === -1 ? 9999 : ia) - (ib === -1 ? 9999 : ib);
                    });
                    return (
                      <div className="space-y-2">
                        {sorted.map((p, i) => (
                          <React.Fragment key={p.id || i}>
                            {/* Separador titular → banco */}
                            {i === ppt && (
                              <div className="flex items-center gap-2 py-1">
                                <div className="flex-1 h-px bg-slate-200" />
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">🪑 Banco</span>
                                <div className="flex-1 h-px bg-slate-200" />
                              </div>
                            )}
                            <div className="flex items-center gap-3 bg-white border border-slate-100 rounded-xl p-3">
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${i < ppt ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-400'}`}>
                                {i < ppt ? i + 1 : '—'}
                              </div>
                              <PlayerAvatar user={p} size="sm" />
                              <div className="flex-1 min-w-0">
                                <div className="font-bold text-slate-700 text-sm truncate">{p.name}</div>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <PositionBadge position={p.position} />
                                  <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><Footprints className="w-2.5 h-2.5" /> {p.dominantFoot || 'Destro'}</span>
                                  {p.heartTeam && <TeamFlag team={p.heartTeam} />}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <div className="text-right">
                                  <div className="font-bold text-indigo-600 text-sm">{getR(p)?.toFixed(1) || '3.0'}</div>
                                  <div className="text-[9px] text-slate-400">{ratingLabel}</div>
                                </div>
                                {isOrganizer && (
                                  <button
                                    onClick={() => handleMovePlayer(p, activeTeamTab)}
                                    className="ml-1 p-1.5 text-slate-300 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                    title={`Mover para Time ${activeTeamTab === 'A' ? 'B' : 'A'}`}
                                  >
                                    <ArrowLeftRight className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </React.Fragment>
                        ))}
                      </div>
                    );
                  })()
                )}
              </div>
            ) : (
              isOrganizer && selectedGame.confirmedIds.length > 1 && (
                <div className="mb-8 p-4 bg-indigo-50 rounded-xl border border-indigo-100">
                  <div className="flex justify-between items-center mb-3">
                    <div className="text-indigo-900 font-bold text-sm">Configuração</div>
                    <button onClick={() => setShowDrawSettings(!showDrawSettings)} className="p-1 hover:bg-indigo-100 rounded"><Settings className="w-4 h-4 text-indigo-600" /></button>
                  </div>
                  {showDrawSettings && (
                    <div className="mb-4 bg-white p-3 rounded-lg border border-indigo-100 text-sm space-y-2 animate-in slide-in-from-top-2">
                      <p className="font-bold text-slate-700 text-xs uppercase">Critério do Sorteio:</p>
                      {[
                        {id: 'balanced_manual', label: 'Equilibrado (Nível Manual)'},
                        {id: 'balanced_community', label: 'Equilibrado (Nível da Galera)'},
                        {id: 'balanced_admin', label: 'Equilibrado (Nota do Admin)'},
                        {id: 'heartTeam', label: 'Clássico (Times do Coração)'},
                        {id: 'random', label: 'Aleatório'},
                        {id: 'manual', label: 'Manual — eu monto os times'},
                      ].map(opt => (
                        <label key={opt.id} className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" name="drawMode" checked={drawConfig.mode === opt.id}
                            onChange={() => {
                              if (opt.id !== 'heartTeam') { setDrawConfig({ mode: opt.id }); return; }
                              // Auto-detecta os 2 clubes mais representados entre os confirmados
                              const counts = {};
                              selectedGame.confirmedIds.forEach(id => {
                                const club = getPlayer(id)?.heartTeam;
                                if (club && club !== 'Sem Time') counts[club] = (counts[club] || 0) + 1;
                              });
                              const top2 = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 2).map(([c]) => c);
                              setDrawConfig({ mode: 'heartTeam', clubA: top2[0] || 'Grêmio', clubB: top2[1] || 'Internacional' });
                            }}
                            className="accent-indigo-600" />
                          <span className="text-slate-600">{opt.label}</span>
                        </label>
                      ))}
                      {drawConfig.mode === 'heartTeam' && (
                        <div className="mt-1 bg-slate-50 rounded-lg p-3 space-y-2">
                          <p className="text-[10px] text-slate-500 font-medium">Escolha os times do clássico:</p>
                          <div className="flex items-center gap-2">
                            <div className="flex-1">
                              <div className="text-[9px] uppercase font-bold text-indigo-500 mb-1">Time A</div>
                              <select value={drawConfig.clubA || ''} onChange={e => setDrawConfig({...drawConfig, clubA: e.target.value})}
                                className="w-full text-xs p-1.5 border border-slate-200 rounded-lg bg-white outline-none">
                                {TEAMS_OPTIONS.filter(t => t !== 'Sem Time').map(t => <option key={t} value={t}>{t}</option>)}
                              </select>
                            </div>
                            <span className="text-sm font-bold text-slate-400 mt-4">×</span>
                            <div className="flex-1">
                              <div className="text-[9px] uppercase font-bold text-orange-500 mb-1">Time B</div>
                              <select value={drawConfig.clubB || ''} onChange={e => setDrawConfig({...drawConfig, clubB: e.target.value})}
                                className="w-full text-xs p-1.5 border border-slate-200 rounded-lg bg-white outline-none">
                                {TEAMS_OPTIONS.filter(t => t !== 'Sem Time').map(t => <option key={t} value={t}>{t}</option>)}
                              </select>
                            </div>
                          </div>
                          <p className="text-[9px] text-slate-400">Torcedores de outros times e sem time são distribuídos automaticamente para equilibrar.</p>
                        </div>
                      )}
                      {drawConfig.mode === 'balanced_community' && (
                        <div className="flex gap-2 mt-2 bg-slate-50 rounded-lg p-1">
                          {[{ id: 'all', label: 'Desde o início' }, { id: 'recent', label: 'Últimos 4 jogos' }].map(opt => (
                            <button key={opt.id} type="button" onClick={() => setDrawConfig({...drawConfig, ratingPeriod: opt.id})}
                              className={`flex-1 py-1 text-[10px] font-bold rounded transition-colors ${(drawConfig.ratingPeriod || 'all') === opt.id ? 'bg-indigo-600 text-white' : 'text-slate-400'}`}>
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      )}
                      {/* Ignorar posições — só faz sentido nos modos equilibrados */}
                      {drawConfig.mode?.startsWith('balanced_') && (
                        <div className="mt-2 pt-2 border-t border-slate-100">
                          <button
                            type="button"
                            onClick={() => setDrawConfig({ ...drawConfig, ignorePositions: !drawConfig.ignorePositions })}
                            className="w-full flex items-center justify-between gap-2"
                          >
                            <div className="text-left">
                              <div className="text-xs font-bold text-slate-700">Ignorar posições</div>
                              <div className="text-[10px] text-slate-400 leading-tight">Equilibra só pela nota, sem agrupar por posição</div>
                            </div>
                            <span className={`relative w-10 h-5 rounded-full transition-colors shrink-0 ${drawConfig.ignorePositions ? 'bg-indigo-600' : 'bg-slate-300'}`}>
                              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${drawConfig.ignorePositions ? 'left-[22px]' : 'left-0.5'}`} />
                            </span>
                          </button>
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                        <span className="text-xs font-bold text-slate-700">Titulares por time:</span>
                        <input type="number" min="3" max="11" value={drawConfig.playersPerTeam || selectedGame.playersPerTeam || 6}
                          onChange={e => setDrawConfig({...drawConfig, playersPerTeam: parseInt(e.target.value) || 6})}
                          className="w-16 p-1.5 text-center border border-slate-200 rounded-lg text-sm font-bold" />
                      </div>

                      {/* Informativo: como funciona cada critério */}
                      <div className="mt-2 pt-2 border-t border-slate-100">
                        <button type="button" onClick={() => setShowDrawInfo(s => !s)} className="w-full flex items-center justify-between text-[11px] font-bold text-indigo-600">
                          <span>ℹ️ Como funcionam os sorteios?</span>
                          <span className="text-slate-400">{showDrawInfo ? '▲' : '▼'}</span>
                        </button>
                        {showDrawInfo && (
                          <div className="mt-2 space-y-2 text-[10px] text-slate-500 leading-snug">
                            <p><strong className="text-slate-700">Equilibrado (Nível Manual):</strong> usa o nível auto-avaliado por cada jogador no perfil. Distribui pareando os melhores para que os dois times tenham notas somadas parecidas.</p>
                            <p><strong className="text-slate-700">Equilibrado (Nível da Galera):</strong> usa a nota média que a galera dá nas votações pós-jogo. Pode considerar desde o início ou só os últimos 4 jogos.</p>
                            <p><strong className="text-slate-700">Equilibrado (Nota do Admin):</strong> usa a nota que o admin definiu para cada jogador na aba Membros.</p>
                            <p className="pl-2 border-l-2 border-slate-200"><strong className="text-slate-600">Nos 3 modos equilibrados:</strong> por padrão os jogadores são agrupados por posição (def/meia/ata) antes de equilibrar, garantindo distribuição parecida de posições. Se os times saem desparelhos, ative <strong>“Ignorar posições”</strong> — aí o equilíbrio é feito só pela nota, num pool único (times mais justos no nível, mas posições podem ficar desbalanceadas).</p>
                            <p><strong className="text-slate-700">Clássico (Times do Coração):</strong> torcedores do Time A vão pra um lado, do Time B pro outro. Quem torce por outros clubes ou não tem time é distribuído pela nota para equilibrar.</p>
                            <p><strong className="text-slate-700">Aleatório:</strong> embaralha todo mundo sem considerar nota nem posição. Só mantém o número de jogadores parelho.</p>
                            <p><strong className="text-slate-700">Manual:</strong> você monta os times na mão, arrastando cada jogador.</p>
                            <p className="text-slate-400">Em todos os modos (exceto Aleatório e Ignorar posições), goleiros são separados primeiro — 1 por time quando há 2+.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <Button size="sm" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-none text-xs" onClick={drawConfig.mode === 'manual' ? handleOpenManualDraw : handleDrawTeams}>
                    <Shuffle className="w-3 h-3" />
                    {drawConfig.mode === 'manual' ? 'Montar Times' : 'Sortear Times Agora'}
                  </Button>
                </div>
              )
            )}

            <div>
              <div className="flex justify-between items-end mb-4">
                <h3 className="font-bold text-slate-800 flex items-center gap-2">Lista de Jogadores</h3>
                {isOrganizer && <button onClick={() => setIsAddingGuest(!isAddingGuest)} className="text-xs font-bold text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-blue-100 transition-colors"><UserPlus className="w-3 h-3" /> Add Avulso</button>}
              </div>
              {isAddingGuest && (
                <form onSubmit={handleAddGuest} className="mb-4 bg-slate-50 p-4 rounded-xl border border-slate-200 animate-in fade-in zoom-in-95 duration-200 space-y-3">
                  <input type="text" placeholder="Nome do convidado" className="w-full p-2 text-sm border border-slate-300 rounded-lg outline-none" value={guestForm.name} onChange={(e) => setGuestForm({...guestForm, name: e.target.value})} autoFocus />
                  <select className="w-full p-2 text-sm border border-slate-300 rounded-lg bg-white outline-none" value={guestForm.position} onChange={(e) => setGuestForm({...guestForm, position: e.target.value})}>
                    <option value="Goleiro">Goleiro</option><option value="Defensor">Defensor</option><option value="Meia">Meia</option><option value="Atacante">Atacante</option>
                  </select>
                  <TeamPicker value={guestForm.heartTeam} onChange={(team) => setGuestForm({...guestForm, heartTeam: team})} />
                  <div className="flex items-center gap-2 bg-white p-2 rounded-lg border border-slate-300">
                    <span className="text-xs text-slate-400">Nível Manual</span>
                    <StarRating rating={guestForm.manualRating} setRating={(v) => setGuestForm({...guestForm, manualRating: v})} size="w-4 h-4" />
                  </div>
                  <Button size="sm" type="submit" className="w-full">Adicionar</Button>
                </form>
              )}
              <div className="space-y-3">
                {selectedGame.confirmedIds.map((playerId, index) => {
                  const player = getPlayer(playerId);
                  const isWaitingList = index >= selectedGame.maxPlayers;
                  const effRating = getCommunityRating(player);
                  return (
                    <div key={playerId} className={`flex items-center justify-between p-3 rounded-xl border relative overflow-hidden ${isWaitingList ? 'bg-orange-50 border-orange-100' : 'bg-white border-slate-100'}`}>
                      {player.isGuest && <div className="absolute right-0 top-0 text-[8px] font-bold bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded-bl">AVULSO</div>}
                      {!player.isGuest && selectedGame.confirmedExternalCheckins?.includes(playerId) && <div className="absolute right-0 top-0 text-[8px] font-bold bg-blue-100 text-blue-500 px-1.5 py-0.5 rounded-bl">EXTERNO</div>}
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => !player.isGuest && navigate(`/players/${playerId}`)}
                          className={player.isGuest ? 'cursor-default' : 'cursor-pointer'}
                        >
                          <PlayerAvatar user={player} size="md" />
                        </button>
                        <div>
                          <div className="font-bold text-slate-700 flex items-center gap-2 text-sm">
                            {player.isGuest ? player.name : (
                              <button onClick={() => navigate(`/players/${playerId}`)} className="hover:text-blue-600 transition-colors text-left">{player.name}</button>
                            )}
                            {group.ownerId === playerId && <Shield className="w-3 h-3 text-blue-500" />}
                            {player.heartTeam && <TeamFlag team={player.heartTeam} />}
                          </div>
                          <div className="flex items-center gap-2 mt-1"><PositionBadge position={player.position} />
                            <span className="text-[10px] text-slate-400 font-medium">★ {effRating?.toFixed(1) || '3.0'}</span>
                            <div className="flex items-center text-[10px] text-slate-400 border-l border-slate-200 pl-2 ml-1 gap-1"><Footprints className="w-3 h-3" /> {player.dominantFoot || 'Destro'}</div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {isWaitingList && <span className="text-[10px] font-bold text-orange-600 bg-orange-100 px-2 py-1 rounded">Espera #{index - selectedGame.maxPlayers + 1}</span>}
                        {isOrganizer && playerId !== userId && selectedGame.status === 'scheduled' && (
                          <>
                            <button
                              onClick={() => handleAdminMarkAbsent(playerId)}
                              className="p-1.5 text-amber-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                              title="Marcar como ausente"
                            >
                              <UserX className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setRemovePlayerTarget({ id: playerId, name: player.name })}
                              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remover jogador"
                            >
                              <UserMinus className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desfalques */}
              {(selectedGame.absentIds?.length > 0) && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <h3 className="font-bold text-slate-500 text-sm flex items-center gap-2 mb-3">
                    <UserX className="w-4 h-4 text-red-400" /> Desfalques ({selectedGame.absentIds.length})
                  </h3>
                  <div className="space-y-2">
                    {selectedGame.absentIds.map(playerId => {
                      const player = getPlayer(playerId);
                      const invitedSub = !!(selectedGame.invitedSubs || {})[playerId];
                      return (
                        <div key={playerId} className={`flex items-center gap-3 p-3 rounded-xl border ${invitedSub ? 'border-emerald-100 bg-emerald-50' : 'border-red-100 bg-red-50'}`}>
                          <button
                            onClick={() => !player.isGuest && navigate(`/players/${playerId}`)}
                            className={player.isGuest ? 'cursor-default' : 'cursor-pointer'}
                          >
                            <PlayerAvatar user={player} size="sm" />
                          </button>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-slate-600 text-sm">{player.name}</div>
                            <div className="flex items-center gap-1 mt-0.5">
                              <PositionBadge position={player.position} />
                              {invitedSub && (
                                <span className="text-[10px] font-bold text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded-full flex items-center gap-1">
                                  ✅ Indicou substituto
                                </span>
                              )}
                            </div>
                          </div>
                          {isOrganizer && selectedGame.status === 'scheduled' && (
                            <div className="flex items-center gap-1 shrink-0">
                              {/* Confirmar presença */}
                              <button
                                onClick={() => handleAdminConfirmPlayer(playerId)}
                                className="p-1.5 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
                                title="Confirmar presença"
                              >
                                <UserCheck className="w-4 h-4" />
                              </button>
                              {/* Toggle: convidou substituto */}
                              <button
                                onClick={() => handleAdminToggleInvitedSub(playerId)}
                                className={`p-1.5 rounded-lg transition-colors text-xs font-bold ${invitedSub ? 'text-emerald-600 bg-emerald-100 hover:bg-emerald-200' : 'text-slate-400 hover:text-slate-600 hover:bg-white'}`}
                                title={invitedSub ? 'Remover isenção de falta' : 'Indicou substituto (isenta penalidade)'}
                              >
                                🔄
                              </button>
                              {/* Remover dos desfalques */}
                              <button
                                onClick={() => updateGame(groupId, gameId, { absentIds: (selectedGame.absentIds || []).filter(id => id !== playerId) })}
                                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-white rounded-lg transition-colors"
                                title="Remover dos desfalques"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Membros sem resposta — admin pode confirmar ou marcar ausente */}
        {isOrganizer && selectedGame.status === 'scheduled' && (() => {
          const allIds = [
            ...(selectedGame.confirmedIds || []),
            ...(selectedGame.absentIds || []),
          ];
          const pending = (group?.memberIds || []).filter(id => !allIds.includes(id));
          if (pending.length === 0) return null;
          return (
            <div className="px-6 mb-6">
              <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                Sem resposta
                <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                  {pending.length}
                </span>
              </h3>
              <div className="space-y-2">
                {pending.map(memberId => {
                  const player = getPlayer(memberId);
                  return (
                    <div key={memberId} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 bg-slate-50">
                      <PlayerAvatar user={player} size="sm" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-700 text-sm truncate">{player.name}</div>
                        <PositionBadge position={player.position} />
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => handleAdminConfirmPlayer(memberId)}
                          className="flex items-center gap-1 text-xs font-bold text-emerald-600 bg-emerald-50 hover:bg-emerald-100 px-2.5 py-1.5 rounded-lg transition-colors"
                          title="Confirmar presença"
                        >
                          <UserCheck className="w-3.5 h-3.5" /> Vai
                        </button>
                        <button
                          onClick={() => handleAdminMarkAbsent(memberId)}
                          className="flex items-center gap-1 text-xs font-bold text-red-500 bg-red-50 hover:bg-red-100 px-2.5 py-1.5 rounded-lg transition-colors"
                          title="Marcar ausente"
                        >
                          <UserX className="w-3.5 h-3.5" /> Falta
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* External checkin requests (admin only) */}
        {isOrganizer && selectedGame.status === 'scheduled' && selectedGame.externalCheckins?.length > 0 && (
          <div className="px-6 mb-6">
            <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
              Check-ins Externos
              <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                {selectedGame.externalCheckins.filter(id => !selectedGame.confirmedExternalCheckins?.includes(id)).length} pendente{selectedGame.externalCheckins.filter(id => !selectedGame.confirmedExternalCheckins?.includes(id)).length !== 1 ? 's' : ''}
              </span>
            </h3>
            <div className="space-y-2">
              {selectedGame.externalCheckins.map(extId => {
                const extUser = users[extId] || { photo: '👤', name: 'Carregando...' };
                const isConfirmed = selectedGame.confirmedExternalCheckins?.includes(extId);
                return (
                  <div key={extId} className={`flex items-center justify-between p-3 rounded-xl border ${isConfirmed ? 'bg-emerald-50 border-emerald-100' : 'bg-amber-50 border-amber-100'}`}>
                    <div className="flex items-center gap-3">
                      <PlayerAvatar user={extUser} size="sm" />
                      <div>
                        <div className="font-bold text-slate-700 text-sm">{extUser.name}</div>
                        <div className="text-[10px] text-slate-400">{isConfirmed ? 'Confirmado' : 'Aguardando confirmacao'}</div>
                      </div>
                    </div>
                    {!isConfirmed && (
                      <div className="flex gap-2">
                        <button onClick={() => approveExternalCheckin(groupId, gameId, extId)} className="p-2 text-emerald-600 hover:bg-emerald-100 rounded-lg transition-colors">
                          <UserCheck className="w-5 h-5" />
                        </button>
                        <button onClick={() => rejectExternalCheckin(groupId, gameId, extId)} className="p-2 text-red-400 hover:bg-red-100 rounded-lg transition-colors">
                          <UserX className="w-5 h-5" />
                        </button>
                      </div>
                    )}
                    {isConfirmed && (
                      <div className="flex items-center gap-2">
                        {extUser.phone && (
                          <a
                            href={`https://wa.me/55${extUser.phone.replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 text-emerald-500 hover:bg-emerald-100 rounded-lg transition-colors"
                            title="WhatsApp"
                          >
                            <MessageCircle className="w-4 h-4" />
                          </a>
                        )}
                        <span className="text-[10px] font-bold text-emerald-600 bg-emerald-100 px-2 py-1 rounded">Externo</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Painel da Partida — tela cheia para marcar gols/assistências durante o jogo */}
        {showFinishModal && (
          <div className="fixed inset-0 z-[60] bg-slate-50 flex flex-col" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
            {/* Header */}
            <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between shrink-0">
              <button
                onClick={() => { setShowFinishModal(false); setShowStatsInput(false); setShowZeroScoreConfirm(false); setShowStatsMismatchConfirm(false); }}
                className="p-2 -ml-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
                aria-label="Fechar"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <h2 className="font-bold text-slate-800 text-base">Painel da Partida</h2>
                {isLive && (
                  <span className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wide text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse" /> Ao vivo · {fmtElapsed}
                  </span>
                )}
              </div>
              {isLive ? (
                <div className="flex items-center gap-1">
                  <button onClick={() => setShowStopLiveModal(true)} className="text-xs font-medium text-red-500 hover:bg-red-50 px-2 py-1.5 rounded-lg transition-colors">Cancelar</button>
                  <button onClick={() => setShowFinishModal(false)} className="text-xs font-bold text-emerald-600 hover:bg-emerald-50 px-2 py-1.5 rounded-lg transition-colors">Minimizar</button>
                </div>
              ) : (
                <div className="w-9" />
              )}
            </div>

            {/* Placar grande sticky */}
            <div className="bg-white border-b border-slate-200 px-4 py-4 shrink-0">
              <div className="flex items-center justify-center gap-4 sm:gap-6">
                <div className="text-center flex-1 max-w-[8rem]">
                  <div className="text-xs uppercase font-bold tracking-wide text-indigo-500 mb-2">Time A</div>
                  <NumericInput
                    className="w-full h-20 text-center text-5xl font-black bg-indigo-50 border-2 border-indigo-100 rounded-2xl outline-indigo-500 text-indigo-700"
                    value={matchScore.teamA}
                    onChange={(v) => setMatchScore({...matchScore, teamA: v})}
                    min={0}
                    max={99}
                  />
                </div>
                <div className="text-3xl font-bold text-slate-300 pt-7">×</div>
                <div className="text-center flex-1 max-w-[8rem]">
                  <div className="text-xs uppercase font-bold tracking-wide text-orange-500 mb-2">Time B</div>
                  <NumericInput
                    className="w-full h-20 text-center text-5xl font-black bg-orange-50 border-2 border-orange-100 rounded-2xl outline-orange-500 text-orange-700"
                    value={matchScore.teamB}
                    onChange={(v) => setMatchScore({...matchScore, teamB: v})}
                    min={0}
                    max={99}
                  />
                </div>
              </div>
              <p className="text-[11px] text-center text-slate-400 mt-3">
                {group.skipVoting ? 'Marque os gols e finalize o jogo.' : 'Marque os gols ao longo da partida e finalize quando terminar.'}
              </p>
              {selectedGame.teams && (
                <button
                  type="button"
                  onClick={() => setAutoCountScore(v => !v)}
                  className={`mt-2 w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-xs font-medium transition-colors ${autoCountScore ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                >
                  <span className={`w-8 h-4 rounded-full relative transition-colors ${autoCountScore ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                    <span className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform ${autoCountScore ? 'left-[18px]' : 'left-0.5'}`} />
                  </span>
                  Contabilizar gols no placar automaticamente
                </button>
              )}
              {/* Gol contra — ponto para o time, sem atribuir a jogador */}
              {selectedGame.teams && (
                <div className="mt-2 flex items-center justify-center gap-2 bg-slate-50 rounded-lg py-1.5 px-2">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400 shrink-0">Gol contra</span>
                  {[
                    { key: 'teamA', label: 'Time A', text: 'text-indigo-600' },
                    { key: 'teamB', label: 'Time B', text: 'text-orange-600' },
                  ].map(t => (
                    <div key={t.key} className="flex items-center gap-0.5 bg-white border border-slate-200 rounded-full p-0.5">
                      <button type="button" onClick={() => addOwnGoal(t.key, -1)} disabled={(ownGoals[t.key] || 0) === 0} className="w-6 h-6 rounded-full flex items-center justify-center font-bold text-slate-400 hover:text-red-500 disabled:opacity-30 active:scale-95 transition-all">−</button>
                      <span className={`text-[10px] font-bold px-1 ${t.text}`}>{t.label} {ownGoals[t.key] ? `+${ownGoals[t.key]}` : ''}</span>
                      <button type="button" onClick={() => addOwnGoal(t.key, 1)} className="w-6 h-6 rounded-full bg-slate-700 text-white flex items-center justify-center font-bold hover:bg-slate-800 active:scale-95 transition-all">+</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Lista de jogadores com +/- gols/assistências */}
            {selectedGame.teams ? (
              <>
                {/* ── DESKTOP/TABLET (lg+): dois times lado a lado ── */}
                <div className="hidden lg:grid grid-cols-2 gap-4 flex-1 overflow-y-auto px-4 py-4 w-full max-w-5xl mx-auto">
                  {[
                    { id: 'A', label: 'Time A', headBg: 'bg-indigo-50', headText: 'text-indigo-700', dot: 'bg-indigo-500', players: selectedGame.teams.teamA || [] },
                    { id: 'B', label: 'Time B', headBg: 'bg-orange-50', headText: 'text-orange-700', dot: 'bg-orange-500', players: selectedGame.teams.teamB || [] },
                  ].map(team => {
                    const teamGoals = team.players.reduce((s, p) => s + (playerStats[p.id]?.goals || 0), 0);
                    return (
                      <div key={team.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden self-start">
                        <div className={`flex items-center justify-between px-4 py-2.5 ${team.headBg} sticky top-0`}>
                          <div className="flex items-center gap-2">
                            <span className={`w-2.5 h-2.5 rounded-full ${team.dot}`} />
                            <span className={`text-sm font-bold ${team.headText}`}>{team.label}</span>
                          </div>
                          <span className={`text-xs font-bold ${team.headText}`}>⚽ {teamGoals}</span>
                        </div>
                        <div className="divide-y divide-slate-100">
                          {team.players.map(p => renderMatchPlayerRow(p, team.id))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* ── MOBILE (<lg): abas por time ── */}
                <div className="lg:hidden bg-white border-b border-slate-200 px-4 flex gap-2 shrink-0">
                  {[
                    { id: 'A', label: 'Time A', activeBg: 'bg-indigo-50', activeText: 'text-indigo-700', barColor: 'bg-indigo-500', players: selectedGame.teams.teamA || [] },
                    { id: 'B', label: 'Time B', activeBg: 'bg-orange-50', activeText: 'text-orange-700', barColor: 'bg-orange-500', players: selectedGame.teams.teamB || [] },
                  ].map(tab => {
                    const isActive = finishTeamTab === tab.id;
                    const teamGoals = tab.players.reduce((s, p) => s + (playerStats[p.id]?.goals || 0), 0);
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setFinishTeamTab(tab.id)}
                        className={`flex-1 py-3 text-sm font-bold transition-colors relative flex items-center justify-center gap-2 ${isActive ? tab.activeText : 'text-slate-400 hover:text-slate-600'}`}
                      >
                        <span>{tab.label}</span>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isActive ? `${tab.activeBg} ${tab.activeText}` : 'bg-slate-100 text-slate-500'}`}>
                          ⚽ {teamGoals}
                        </span>
                        {isActive && <div className={`absolute bottom-0 left-2 right-2 h-0.5 rounded-full ${tab.barColor}`} />}
                      </button>
                    );
                  })}
                </div>
                <div className="lg:hidden flex-1 overflow-y-auto px-4 py-4">
                  {(() => {
                    const activePlayers = finishTeamTab === 'A'
                      ? (selectedGame.teams.teamA || [])
                      : (selectedGame.teams.teamB || []);
                    return (
                      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                        <div className="divide-y divide-slate-100">
                          {activePlayers.map(p => renderMatchPlayerRow(p, finishTeamTab))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </>
            ) : (
              <div className="flex-1 overflow-y-auto px-4 py-4">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700 text-center">
                  <p className="font-bold mb-1">Times ainda não sorteados</p>
                  <p className="text-xs">Informe só o placar para finalizar. Para registrar gols/assistências por jogador, faça o sorteio primeiro.</p>
                </div>
              </div>
            )}

            {/* Bottom action bar (sticky) */}
            <div className="bg-white border-t border-slate-200 px-4 py-3 shrink-0" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' }}>
          {showZeroScoreConfirm ? (
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800 text-center">
                <p className="font-bold mb-1">Placar 0 × 0?</p>
                <p>Confirma que o jogo terminou mesmo sem gols?</p>
              </div>
              <div className="flex gap-3">
                <Button variant="secondary" className="flex-1" onClick={() => setShowZeroScoreConfirm(false)}>Não, corrigir</Button>
                <Button className="flex-1" onClick={() => { setShowZeroScoreConfirm(false); handleStartVoting(); }}>Sim, foi 0×0</Button>
              </div>
            </div>
          ) : showStatsMismatchConfirm ? (
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
                <p className="font-bold mb-1 text-center">⚠️ Gols não batem com o placar</p>
                <div className="text-xs text-center mt-2 space-y-0.5">
                  <p>Placar: <strong>{matchScore.teamA} × {matchScore.teamB}</strong></p>
                  <p>Gols registrados: Time A <strong>{statsMismatchInfo?.teamAGoals ?? 0}</strong> · Time B <strong>{statsMismatchInfo?.teamBGoals ?? 0}</strong></p>
                </div>
                <p className="text-xs text-center mt-2">Pode ser <strong>gol contra</strong> (não atribuído a nenhum jogador). Continuar mesmo assim?</p>
              </div>
              <div className="flex gap-3">
                <Button variant="secondary" className="flex-1" onClick={() => setShowStatsMismatchConfirm(false)}>Voltar e corrigir</Button>
                <Button className="flex-1" onClick={() => {
                  setShowStatsMismatchConfirm(false);
                  // Se for 0×0 ainda, pedir confirmação adicional
                  if (matchScore.teamA === 0 && matchScore.teamB === 0) { setShowZeroScoreConfirm(true); return; }
                  handleStartVoting();
                }}>Continuar assim</Button>
              </div>
            </div>
          ) : earlyFinish ? (
            // Ao vivo e ainda cedo (< ~60min): finalização de-enfatizada para evitar
            // encerrar o jogo antes da hora. O jogo continua marcável no Painel.
            <div className="space-y-2">
              <div className="flex items-center justify-center gap-2 text-xs font-medium text-red-600 bg-red-50 border border-red-100 rounded-lg py-2">
                <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse" />
                Jogo ao vivo há {fmtElapsed} · normalmente dura ~{EXPECTED_MATCH_MIN}min
              </div>
              <button
                onClick={triggerFinalize}
                className="w-full py-2.5 text-sm font-medium text-slate-500 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
              >
                {group.skipVoting ? 'Encerrar e finalizar mesmo assim' : 'Encerrar e iniciar votação mesmo assim'}
              </button>
            </div>
          ) : (
            <Button className="w-full py-4 text-base shadow-lg shadow-blue-200" onClick={triggerFinalize}>
              {group.skipVoting ? 'Finalizar Partida' : 'Iniciar Votação'}
            </Button>
          )}
            </div>
          </div>
        )}

        <Modal isOpen={showUndoCheckinModal} onClose={() => setShowUndoCheckinModal(false)} title={undoAction === 'markAbsent' ? 'Vai amarelar mesmo? 🐔' : 'Desconfirmar presença?'}>
          <p className="text-sm text-slate-500 mb-6 text-center">
            {undoAction === 'markAbsent'
              ? 'Vamos registrar que você não vai comparecer. Sua vaga será liberada.'
              : 'Se sair agora, sua vaga pode ser liberada para outro jogador.'}
          </p>
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setShowUndoCheckinModal(false)}>Cancelar</Button>
            <Button variant="danger" className="flex-1" onClick={() => {
              if (undoAction === 'markAbsent') handleMarkAbsent();
              else handleUnconfirm();
              setShowUndoCheckinModal(false);
            }}>
              {undoAction === 'markAbsent' ? 'Não vou mesmo' : 'Desconfirmar'}
            </Button>
          </div>
        </Modal>

        <Modal isOpen={!!removePlayerTarget} onClose={() => setRemovePlayerTarget(null)} title="Remover jogador?">
          {selectedGame.status === 'voting' ? (
            <p className="text-sm text-slate-500 mb-6 text-center">
              <strong>{removePlayerTarget?.name}</strong> não apareceu? Removê-lo agora vai tirá-lo dos times e apagar os votos que ele recebeu — ele <strong>não receberá nota nem pontuação</strong> nesta partida.
            </p>
          ) : (
            <p className="text-sm text-slate-500 mb-6 text-center">
              Tem certeza que quer remover <strong>{removePlayerTarget?.name}</strong> da lista de confirmados?
            </p>
          )}
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setRemovePlayerTarget(null)}>Cancelar</Button>
            <Button variant="danger" className="flex-1" onClick={() => selectedGame.status === 'voting' ? handleRemoveDuringVoting(removePlayerTarget.id) : handleAdminRemovePlayer(removePlayerTarget.id)}>Remover</Button>
          </div>
        </Modal>

        <Modal isOpen={showFinalizeModal} onClose={() => setShowFinalizeModal(false)} title="Finalizar e atualizar níveis?">
          <p className="text-sm text-slate-500 mb-6 text-center">
            Os ratings de todos os jogadores votados serão atualizados. Esta ação é <strong>irreversível</strong>.
          </p>
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setShowFinalizeModal(false)}>Cancelar</Button>
            <Button className="flex-1" onClick={async () => { setShowFinalizeModal(false); await handleFinalize(); }}>Confirmar</Button>
          </div>
        </Modal>

        {/* Cancelar ao vivo — overlay próprio com z acima do Painel */}
        {showStopLiveModal && (
          <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowStopLiveModal(false)}>
            <div className="bg-white w-full max-w-sm rounded-2xl p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
              <h3 className="text-xl font-bold text-slate-800 mb-3 text-center">Cancelar jogo ao vivo?</h3>
              <p className="text-sm text-slate-500 text-center mb-2">
                O jogo volta para o estado de <strong>times sorteados</strong> e o cronômetro é zerado.
              </p>
              <p className="text-xs text-slate-400 text-center mb-5">
                O placar e os gols/assistências já marcados <strong>são mantidos</strong> — você pode iniciar ao vivo de novo quando quiser.
              </p>
              <div className="flex gap-3">
                <Button variant="secondary" className="flex-1" onClick={() => setShowStopLiveModal(false)}>Voltar</Button>
                <Button variant="danger" className="flex-1" onClick={handleStopLive}>Cancelar ao vivo</Button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmação final — overlay próprio com z acima do Painel da Partida */}
        {showFinalActionConfirm && (
          <div
            className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowFinalActionConfirm(false)}
          >
            <div
              className="bg-white w-full max-w-sm rounded-2xl p-6 shadow-2xl relative"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-slate-800 mb-4 text-center">
                {group.skipVoting ? 'Finalizar partida?' : 'Iniciar votação?'}
              </h3>
              <div className="space-y-4">
                <p className="text-sm text-slate-500 text-center">
                  {group.skipVoting
                    ? 'A partida será finalizada com o placar abaixo. Esta ação dispara o cálculo de classificação e estatísticas.'
                    : 'A votação será iniciada com o placar abaixo. Os jogadores poderão começar a avaliar.'}
                </p>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center justify-center gap-4">
                    <div className="text-center">
                      <div className="text-[10px] uppercase font-bold text-indigo-500">Time A</div>
                      <div className="text-3xl font-black text-indigo-700">{matchScore.teamA}</div>
                    </div>
                    <div className="text-2xl text-slate-300">×</div>
                    <div className="text-center">
                      <div className="text-[10px] uppercase font-bold text-orange-500">Time B</div>
                      <div className="text-3xl font-black text-orange-700">{matchScore.teamB}</div>
                    </div>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 text-center">
                  Caso esteja errado, dá pra desfazer voltando ao Painel da Partida — mas é mais fácil conferir agora.
                </p>
                <div className="flex gap-3">
                  <Button variant="secondary" className="flex-1" onClick={() => setShowFinalActionConfirm(false)}>
                    Voltar
                  </Button>
                  <Button className="flex-1" onClick={() => { setShowFinalActionConfirm(false); handleStartVoting(); }}>
                    {group.skipVoting ? 'Sim, finalizar' : 'Sim, iniciar votação'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <Modal isOpen={!!incompleteVoteConfirm} onClose={() => setIncompleteVoteConfirm(null)} title="Enviar com avaliações faltando?">
          {incompleteVoteConfirm && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
                <p className="text-sm font-bold text-amber-800 mb-1">
                  Você avaliou {incompleteVoteConfirm.totalCount - incompleteVoteConfirm.unvotedCount} de {incompleteVoteConfirm.totalCount} jogadores
                </p>
                <p className="text-xs text-amber-700">
                  Faltam <strong>{incompleteVoteConfirm.unvotedCount}</strong> jogador{incompleteVoteConfirm.unvotedCount !== 1 ? 'es' : ''} sem avaliação.
                </p>
                <p className="text-xs text-amber-600 mt-2">
                  Lembre-se: as avaliações estão divididas por time. Verifique as outras abas antes de enviar.
                </p>
              </div>
              <div className="flex gap-3">
                <Button variant="secondary" className="flex-1" onClick={() => setIncompleteVoteConfirm(null)}>
                  Voltar e avaliar
                </Button>
                <Button className="flex-1" onClick={() => { setIncompleteVoteConfirm(null); handleSubmitVotes(); setEditingVotes(false); }}>
                  Enviar assim
                </Button>
              </div>
            </div>
          )}
        </Modal>

        <Modal isOpen={showRevertVotingModal} onClose={() => setShowRevertVotingModal(false)} title="Voltar para agendado?">
          {(() => {
            const votedCount = Object.keys(selectedGame?.votes || {}).length;
            return (
              <>
                <p className="text-sm text-slate-500 mb-4 text-center">
                  O jogo voltará para o status <strong>Agendado</strong> e <strong>todos os votos já registrados serão apagados</strong>.
                </p>
                {votedCount > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 text-center">
                    <p className="text-sm font-bold text-red-700">
                      ⚠️ {votedCount} {votedCount === 1 ? 'jogador já votou' : 'jogadores já votaram'}
                    </p>
                    <p className="text-xs text-red-600 mt-1">Os votos serão perdidos e precisarão ser refeitos.</p>
                  </div>
                )}
                <div className="flex gap-3">
                  <Button variant="secondary" className="flex-1" onClick={() => setShowRevertVotingModal(false)}>Cancelar</Button>
                  <Button variant="danger" className="flex-1" onClick={async () => {
                    setShowRevertVotingModal(false);
                    const lostVotes = Object.keys(selectedGame?.votes || {}).length;
                    await updateGame(groupId, gameId, { status: 'scheduled', votes: {} });
                    logAuditEvent && logAuditEvent(groupId, 'game.voting_reverted', gameId, {
                      votesLost: lostVotes,
                      voterIds: Object.keys(selectedGame?.votes || {}),
                    });
                    showSuccess('Votação cancelada — jogo voltou para agendado.');
                  }}>
                    Voltar mesmo
                  </Button>
                </div>
              </>
            );
          })()}
        </Modal>

        <Modal isOpen={showForceFinishModal} onClose={() => setShowForceFinishModal(false)} title="Finalizar sem votação?">
          <p className="text-sm text-slate-500 mb-6 text-center">
            Esta ação é <strong>irreversível</strong>. O jogo será computado no histórico sem ratings e o próximo jogo será criado automaticamente.
          </p>
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setShowForceFinishModal(false)}>Cancelar</Button>
            <Button variant="danger" className="flex-1" onClick={async () => { setShowForceFinishModal(false); await finalizeGame(groupId, gameId); }}>Finalizar mesmo</Button>
          </div>
        </Modal>

        <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Excluir este jogo?">
          <p className="text-sm text-slate-500 mb-6 text-center">
            O jogo será removido permanentemente. Esta ação não pode ser desfeita.
          </p>
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setShowDeleteModal(false)}>Cancelar</Button>
            <Button variant="danger" className="flex-1" onClick={async () => { setShowDeleteModal(false); await deleteGame(groupId, gameId); showSuccess('Jogo excluído.'); navigate(-1); }}>Excluir</Button>
          </div>
        </Modal>

      </div>

      {/* ── Modal de montagem manual de times ── */}
      <Modal isOpen={showManualDraw} onClose={() => setShowManualDraw(false)} title="Montar Times">
        {(() => {
          const confirmedPlayers = selectedGame.confirmedIds.map(id => ({ ...getPlayer(id), id }));
          const sortPos = (a, b) =>
            ({'Goleiro':0,'Defensor':1,'Meia':2,'Atacante':3}[a.position]||9) -
            ({'Goleiro':0,'Defensor':1,'Meia':2,'Atacante':3}[b.position]||9);
          const teamAPlayers = confirmedPlayers.filter(p => manualAssignment[p.id] === 'A').sort(sortPos);
          const teamBPlayers = confirmedPlayers.filter(p => manualAssignment[p.id] === 'B').sort(sortPos);
          const unassigned   = confirmedPlayers.filter(p => !manualAssignment[p.id]);

          const assign = (playerId, team) => {
            setManualAssignment(prev => {
              const next = { ...prev };
              if (next[playerId] === team) delete next[playerId]; // toggle off = volta para sem time
              else next[playerId] = team;
              return next;
            });
          };

          const PlayerRow = ({ p, team }) => (
            <div className="flex items-center gap-2 py-1.5">
              <PlayerAvatar user={p} size="xs" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-slate-700 truncate">{p.name}</div>
                <PositionBadge position={p.position} />
              </div>
              <div className="flex gap-1 shrink-0">
                <button
                  onClick={() => assign(p.id, 'A')}
                  className={`w-8 h-8 rounded-lg text-xs font-bold transition-colors ${team === 'A' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500 hover:bg-indigo-100'}`}
                >A</button>
                <button
                  onClick={() => assign(p.id, 'B')}
                  className={`w-8 h-8 rounded-lg text-xs font-bold transition-colors ${team === 'B' ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-orange-100'}`}
                >B</button>
              </div>
            </div>
          );

          return (
            <div className="space-y-4">
              {/* Contadores */}
              <div className="flex gap-2">
                <div className="flex-1 bg-indigo-50 rounded-xl p-2 text-center">
                  <div className="text-lg font-bold text-indigo-700">{teamAPlayers.length}</div>
                  <div className="text-[10px] font-bold text-indigo-400 uppercase">Time A</div>
                </div>
                <div className="flex-1 bg-slate-100 rounded-xl p-2 text-center">
                  <div className="text-lg font-bold text-slate-500">{unassigned.length}</div>
                  <div className="text-[10px] font-bold text-slate-400 uppercase">Sem time</div>
                </div>
                <div className="flex-1 bg-orange-50 rounded-xl p-2 text-center">
                  <div className="text-lg font-bold text-orange-600">{teamBPlayers.length}</div>
                  <div className="text-[10px] font-bold text-orange-400 uppercase">Time B</div>
                </div>
              </div>

              {/* Lista de jogadores — ordem fixa, sem reordenar ao marcar */}
              <div className="max-h-80 overflow-y-auto space-y-0.5 divide-y divide-slate-50">
                {confirmedPlayers.map(p => <PlayerRow key={p.id} p={p} team={manualAssignment[p.id] || null} />)}
              </div>

              <p className="text-[10px] text-slate-400 text-center">
                Toque em A ou B para distribuir. Toque novamente no botão ativo para tirar do time.
              </p>

              {/* Ações */}
              <div className="flex gap-2 pt-1">
                <Button variant="secondary" className="flex-1" onClick={() => setShowManualDraw(false)}>Cancelar</Button>
                <Button
                  className="flex-1"
                  disabled={unassigned.length > 0}
                  onClick={handleSaveManualTeams}
                  title={unassigned.length > 0 ? `${unassigned.length} jogador(es) sem time` : ''}
                >
                  {unassigned.length > 0 ? `Faltam ${unassigned.length}` : 'Salvar Times'}
                </Button>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* ── Modal de foto do jogador ── */}
      {photoModal && (
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/75 backdrop-blur-sm"
          onClick={() => setPhotoModal(null)}
        >
          <div className="flex flex-col items-center px-6" onClick={e => e.stopPropagation()}>
            {photoModal.photoURL ? (
              <img
                src={photoModal.photoURL}
                alt={photoModal.name}
                className="w-44 h-44 rounded-full ring-4 ring-white shadow-2xl object-cover"
                crossOrigin="anonymous"
              />
            ) : (
              <div className="w-44 h-44 rounded-full bg-white ring-4 ring-white shadow-2xl flex items-center justify-center text-8xl">
                {photoModal.photo || '👤'}
              </div>
            )}
            <p className="text-white font-bold text-xl mt-4 text-center drop-shadow">{photoModal.name}</p>
            {photoModal.heartTeam && photoModal.heartTeam !== 'Sem Time' && (
              <div className="mt-2 flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-1.5 rounded-full">
                <TeamFlag team={photoModal.heartTeam} />
                <span className="text-white text-sm font-medium">{photoModal.heartTeam}</span>
              </div>
            )}
            <button
              onClick={() => setPhotoModal(null)}
              className="mt-8 text-white/60 text-xs hover:text-white transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default GameDetailsPage;


